import React, { useState, useEffect } from 'react';
import { api, adminApi, API_BASE_URL, resolveMediaUrl } from '../api';
import DocumentUploadSection from '../components/DocumentUploadSection';
import PageContentEditor from '../components/PageContentEditor';

const isImageFile = (url) => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url || '');

function DocLink({ url, label }) {
  const href = resolveMediaUrl(url);
  if (!href) return <span className="text-xs text-gray-400">Not uploaded</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline">
      {isImageFile(href) && <img src={href} alt={label} className="w-14 h-14 object-cover rounded border" />}
      <span>View {label}</span>
    </a>
  );
}

const AdminDashboard = ({ navigate }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [activeCategoryTab, setActiveCategoryTab] = useState('all');
  const [errorMsg, setErrorMsg] = useState(''); // global error display
  const [stats, setStats] = useState({
    totalBookings: 0, totalRevenue: 0, pendingBookings: 0,
    totalCustomers: 0, totalPackages: 0, totalOrders: 0
  });
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());
  const [data, setData] = useState({
    bookings: [], packages: [], orders: [], categories: [], items: [], users: [], payments: [],
    contactMessages: [], discountCodes: []
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [passengers, setPassengers] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tourLeaders, setTourLeaders] = useState([]);
  const [bookingRooms, setBookingRooms] = useState([{ id: 1, sharing_type: 'double', num_adults: 1, num_children: 0, num_infants: 0, passengers: [] }]);
  const [discountCode, setDiscountCode] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [packageStatusFilter, setPackageStatusFilter] = useState('all'); // 'all','active','inactive','featured'
  const [discountForm, setDiscountForm] = useState(null);

  const showError = (err) => {
    const msg = err?.message || String(err) || 'Something went wrong';
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(''), 8000);
  };

  const showSuccess = (msg) => {
    setErrorMsg('✅ ' + msg);
    setTimeout(() => setErrorMsg(''), 4000);
  };

  useEffect(() => {
    const admin = localStorage.getItem('adminUser');
    if (!admin) {
      navigate('admin-login');
      return;
    }
    setAdminUser(JSON.parse(admin));
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      console.log('Loading admin data...');
      
      const [bookings, packages, orders, categories, items, users, payments, tourLeadersData, customersData, contactMessages, discountCodes] = await Promise.all([
        adminApi.getBookings(), adminApi.getAllPackages(), adminApi.getOrders(),
        adminApi.getAllCategories(), api.getItems(), adminApi.getUsers(), adminApi.getPayments(),
        adminApi.getTourLeaders(), adminApi.getCustomers(),
        adminApi.getContactMessages(), adminApi.getDiscountCodes()
      ]);

      console.log('Raw data received:', { bookings, packages, orders, categories, items, users, payments, tourLeadersData, customersData });

      const bookingsArray = Array.isArray(bookings) ? bookings : (bookings.results || []);
      const packagesArray = Array.isArray(packages) ? packages : (packages.results || []);
      const ordersArray = Array.isArray(orders) ? orders : (orders.results || []);
      const categoriesArray = Array.isArray(categories) ? categories : (categories.results || []);
      const itemsArray = Array.isArray(items) ? items : (items.results || []);
      const usersArray = Array.isArray(users) ? users : (users.results || []);
      const paymentsArray = Array.isArray(payments) ? payments : (payments.results || []);
      const tourLeadersArray = Array.isArray(tourLeadersData) ? tourLeadersData : (tourLeadersData.results || []);
      const customersArray = Array.isArray(customersData) ? customersData : (customersData.results || []);

      console.log('Processed arrays:', { 
        bookings: bookingsArray.length, 
        packages: packagesArray.length, 
        orders: ordersArray.length,
        categories: categoriesArray.length,
        items: itemsArray.length,
        users: usersArray.length,
        payments: paymentsArray.length,
        customers: customersArray.length
      });
      
      console.log('Orders data sample:', ordersArray.slice(0, 2));

      const totalRevenue = bookingsArray.reduce((sum, b) => sum + parseFloat(b.paid_amount || 0), 0);
      const pendingBookings = bookingsArray.filter(b => b.status === 'pending').length;

      // Year-filtered stats
      const currentYear = statsYear;
      const filteredBookings = currentYear === 'all'
        ? bookingsArray
        : bookingsArray.filter(b => new Date(b.created_at).getFullYear() === parseInt(currentYear));
      const filteredRevenue = currentYear === 'all'
        ? totalRevenue
        : filteredBookings.reduce((sum, b) => sum + parseFloat(b.paid_amount || 0), 0);

      setStats({
        totalBookings: filteredBookings.length, totalRevenue: filteredRevenue,
        pendingBookings: filteredBookings.filter(b => b.status === 'pending').length,
        totalCustomers: new Set(filteredBookings.map(b => b.customer?.email)).size,
        totalPackages: packagesArray.length, totalOrders: ordersArray.length
      });

      setData({
        bookings: bookingsArray, packages: packagesArray, orders: ordersArray,
        categories: categoriesArray, items: itemsArray, users: usersArray, payments: paymentsArray,
        customers: customersArray,
        contactMessages: Array.isArray(contactMessages) ? contactMessages : [],
        discountCodes: Array.isArray(discountCodes) ? discountCodes : []
      });
      
      setTourLeaders(tourLeadersArray);
      
      console.log('Data loaded successfully!');
    } catch (error) {
      console.error('Error loading data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    navigate('landing');
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await adminApi.updateBookingStatus(bookingId, newStatus);
      showSuccess('Booking status updated!');
      loadAllData(); // Reload data
    } catch (error) {
      console.error('Error updating booking status:', error);
      showError(new Error('Failed to update booking status'));
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await adminApi.updateOrderStatus(orderId, newStatus);
      showSuccess('Order status updated!');
      loadAllData(); // Reload data
    } catch (error) {
      console.error('Error updating order status:', error);
      showError(new Error('Failed to update order status'));
    }
  };

  const handleUpdatePaymentStatus = async (paymentId, newStatus) => {
    try {
      await adminApi.updatePaymentStatus(paymentId, newStatus);
      alert(`Payment ${newStatus === 'completed' ? 'accepted' : 'rejected'} successfully!`);
      loadAllData(); // Reload data
    } catch (error) {
      console.error('Error updating payment status:', error);
      showError(new Error('Failed to update payment status'));
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await adminApi.deleteBooking(bookingId);
      showSuccess('Booking deleted!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      showError(new Error('Failed to delete booking'));
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await adminApi.deleteOrder(orderId);
      showSuccess('Order deleted!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting order:', error);
      showError(new Error('Failed to delete order'));
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    if (item) {
      // Properly populate formData — resolve nested objects to IDs
      const populatedForm = {
        ...item,
        category: item.category?.id ?? item.category ?? '',
        tour_leader: item.tour_leader?.id ?? item.tour_leader ?? '',
        itinerary: Array.isArray(item.itinerary) ? item.itinerary : [],
        // Pre-fill room price fields from existing room_prices array
        ...((item.room_prices || []).reduce((acc, rp) => {
          acc[`room_price_${rp.sharing_type}`] = rp.price;
          acc[`room_avail_${rp.sharing_type}`] = rp.available;
          return acc;
        }, {})),
      };
      setFormData(populatedForm);
    } else {
      setFormData({ itinerary: [] });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedItem(null);
    setFormData({});
    setBookingRooms([{ id: 1, sharing_type: 'double', num_adults: 1, num_children: 0, num_infants: 0, passengers: [] }]);
    setDiscountCode('');
  };

  const addRoom = () => {
    const newRoom = {
      id: bookingRooms.length + 1,
      sharing_type: 'double',
      num_adults: 1,
      num_children: 0,
      num_infants: 0,
      passengers: []
    };
    setBookingRooms([...bookingRooms, newRoom]);
  };

  const removeRoom = (roomId) => {
    if (bookingRooms.length > 1) {
      setBookingRooms(bookingRooms.filter(r => r.id !== roomId));
    }
  };

  const updateRoom = (roomId, field, value) => {
    setBookingRooms(bookingRooms.map(room => 
      room.id === roomId ? { ...room, [field]: value } : room
    ));
  };

  const getTotalPax = (room) => {
    return parseInt(room.num_adults || 0) + parseInt(room.num_children || 0) + parseInt(room.num_infants || 0);
  };

  const addPassengerToRoom = (roomId) => {
    setBookingRooms(bookingRooms.map(room => {
      if (room.id === roomId) {
        const newPassenger = {
          id: (room.passengers.length + 1),
          full_name: '',
          date_of_birth: '',
          gender: '',
          phone: '',
          passport_number: '',
          passport_expiry: '',
          passport_issue_date: '',
          passenger_type: 'adult'
        };
        return { ...room, passengers: [...room.passengers, newPassenger] };
      }
      return room;
    }));
  };

  const removePassengerFromRoom = (roomId, passengerId) => {
    setBookingRooms(bookingRooms.map(room => {
      if (room.id === roomId) {
        return { ...room, passengers: room.passengers.filter(p => p.id !== passengerId) };
      }
      return room;
    }));
  };

  const updatePassenger = (roomId, passengerId, field, value) => {
    setBookingRooms(bookingRooms.map(room => {
      if (room.id === roomId) {
        return {
          ...room,
          passengers: room.passengers.map(p =>
            p.id === passengerId ? { ...p, [field]: value } : p
          )
        };
      }
      return room;
    }));
  };

  const applyDiscount = async () => {
    if (!discountCode) {
      showError(new Error('Please enter a discount code'));
      return;
    }
    try {
      const result = await api.validateDiscountCode(discountCode, formData.total_amount || 0);
      if (result.valid) {
        // setDiscountAmount(result.discount_amount);
        alert(`Discount applied: $${result.discount_amount}`);
      } else {
        showError(new Error(result.message || 'Invalid discount code'));
        // setDiscountAmount(0);
      }
    } catch (error) {
      // console.error('Error validating discount:', error);
      // alert('Failed to validate discount code');
      // setDiscountAmount(0);
    }
  };


  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitForm = async () => {
    try {
      if (modalType === 'add-package') {
        // Use FormData to support file uploads
        const pkgForm = new FormData();
        const fields = ['name','slug','category','short_description','description','location',
          'travel_date','return_date','duration_days','duration_nights','max_capacity',
          'min_deposit_amount','is_featured','is_active','hotel_name','hotel_star_rating',
          'hotel_country','complimentary_items','tour_leader','inclusions','exclusions'];
        fields.forEach(f => {
          if (formData[f] !== undefined && formData[f] !== '') pkgForm.append(f, formData[f]);
        });
        // Handle itinerary JSON
        if (formData.itinerary && formData.itinerary.length > 0) {
          pkgForm.append('itinerary', JSON.stringify(formData.itinerary));
        }
        if (formData._featured_image_file) pkgForm.append('featured_image', formData._featured_image_file);
        if (formData._hotel_image_file) pkgForm.append('hotel_image', formData._hotel_image_file);

        const newPkg = await adminApi.createPackage(pkgForm);
        // Save room prices for newly created package
        if (newPkg && newPkg.id) {
          const roomTypes = ['single', 'double', 'triple', 'quad', 'child_with_bed', 'child_no_bed', 'infant'];
          for (const key of roomTypes) {
            const price = formData[`room_price_${key}`];
            if (price !== undefined && price !== '') {
              const avail = formData[`room_avail_${key}`] ?? true;
              await fetch(`${API_BASE_URL}/admin/packages/${newPkg.id}/update_room_price/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sharing_type: key, price: parseFloat(price), available: avail })
              });
            }
          }
        }
        showSuccess('Package created successfully!');
      } else if (modalType === 'edit-package') {
        // Use FormData to support file uploads
        const pkgForm = new FormData();
        const fields = ['name','slug','short_description','description','location',
          'travel_date','return_date','duration_days','duration_nights','max_capacity',
          'min_deposit_amount','is_featured','is_active','hotel_name','hotel_star_rating',
          'hotel_country','complimentary_items','tour_leader','inclusions','exclusions'];
        fields.forEach(f => {
          const val = formData[f] !== undefined ? formData[f] : selectedItem[f];
          if (val !== undefined && val !== null) pkgForm.append(f, val);
        });
        // Category ID
        const catId = parseInt(formData.category) || (selectedItem.category?.id ?? selectedItem.category);
        pkgForm.append('category', catId);
        // Handle itinerary JSON
        if (formData.itinerary && formData.itinerary.length > 0) {
          pkgForm.append('itinerary', JSON.stringify(formData.itinerary));
        } else if (selectedItem.itinerary) {
          pkgForm.append('itinerary', JSON.stringify(selectedItem.itinerary));
        }
        // Images — only append if new file selected
        if (formData._featured_image_file) pkgForm.append('featured_image', formData._featured_image_file);
        if (formData._hotel_image_file) pkgForm.append('hotel_image', formData._hotel_image_file);

        await adminApi.updatePackage(selectedItem.id, pkgForm);
        // Save room prices if any were set
        const roomTypes = ['single', 'double', 'triple', 'quad', 'child_with_bed', 'child_no_bed', 'infant'];
        for (const key of roomTypes) {
          const price = formData[`room_price_${key}`];
          if (price !== undefined && price !== '') {
            const avail = formData[`room_avail_${key}`] ?? true;
            const existing = formData.room_prices?.find(r => r.sharing_type === key);
            if (existing) {
              await fetch(`${API_BASE_URL}/admin/packages/${selectedItem.id}/update_room_price/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sharing_type: key, price: parseFloat(price), available: avail })
              });
            } else {
              await fetch(`${API_BASE_URL}/admin/packages/${selectedItem.id}/update_room_price/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sharing_type: key, price: parseFloat(price), available: avail })
              });
            }
          }
        }
        showSuccess('Package updated successfully!');
      } else if (modalType === 'add-category') {
        const catForm = new FormData();
        ['name', 'slug', 'description', 'category_type', 'is_active', 'order'].forEach(f => {
          if (formData[f] !== undefined) catForm.append(f, formData[f]);
        });
        if (formData._category_image_file) catForm.append('image', formData._category_image_file);
        await adminApi.createCategory(catForm);
        showSuccess('Category created successfully!');
      } else if (modalType === 'edit-category') {
        const catForm = new FormData();
        ['name', 'slug', 'description', 'category_type', 'is_active', 'order'].forEach(f => {
          const val = formData[f] !== undefined ? formData[f] : selectedItem[f];
          if (val !== undefined) catForm.append(f, val);
        });
        if (formData._category_image_file) catForm.append('image', formData._category_image_file);
        await adminApi.updateCategory(selectedItem.id, catForm);
        showSuccess('Category updated successfully!');
      } else if (modalType === 'add-item') {
        const itemForm = new FormData();
        ['name', 'slug', 'description', 'price', 'stock_quantity', 'category', 'is_active'].forEach(f => {
          if (formData[f] !== undefined) itemForm.append(f, formData[f]);
        });
        if (formData._item_image_file) itemForm.append('image', formData._item_image_file);
        await adminApi.createItem(itemForm);
        showSuccess('Item created successfully!');
      } else if (modalType === 'edit-item') {
        const itemForm = new FormData();
        ['name', 'slug', 'description', 'price', 'stock_quantity', 'category', 'is_active'].forEach(f => {
          const val = formData[f] !== undefined ? formData[f] : selectedItem[f];
          if (val !== undefined) itemForm.append(f, val);
        });
        if (formData._item_image_file) itemForm.append('image', formData._item_image_file);
        await adminApi.updateItem(selectedItem.id, itemForm);
        showSuccess('Item updated successfully!');
      } else if (modalType === 'add-user') {
        await adminApi.createUser(formData);
        showSuccess('User created successfully!');
      } else if (modalType === 'add-booking') {
        await handleCreateWalkInBooking();
        return; // Return early as handleCreateWalkInBooking handles closing
      } else if (modalType === 'add-payment') {
        await handleCreateManualPayment();
        return; // Return early as handleCreateManualPayment handles closing
      }
      closeModal();
      loadAllData();
    } catch (error) {
      console.error('Error submitting form:', error);
      showError(error);
    }
  };

  const handleCreateWalkInBooking = async () => {
    try {
      // Validate required fields
      if (!formData.package_id || !formData.customer_email || !formData.contact_name || 
          !formData.contact_phone || !formData.num_adults || !formData.payment_amount ||
          !formData.passenger_name || !formData.passenger_dob || !formData.passenger_gender ||
          !formData.passenger_phone || !formData.passenger_passport || !formData.passenger_passport_expiry) {
        showError(new Error('Please fill in all required fields including passenger details'));
        return;
      }

      // Prepare passenger data
      const passengerData = {
        full_name: formData.passenger_name,
        date_of_birth: formData.passenger_dob,
        gender: formData.passenger_gender,
        phone: formData.passenger_phone,
        passport_number: formData.passenger_passport,
        passport_expiry: formData.passenger_passport_expiry,
        passport_issue_date: formData.passenger_passport_issue || ''
      };

      // Prepare booking data
      const bookingData = {
        package_id: formData.package_id,
        customer_email: formData.customer_email,
        rooms: [{
          sharing_type: formData.room_type || 'double',
          num_adults: parseInt(formData.num_adults) || 1,
          num_children: parseInt(formData.num_children) || 0,
          num_infants: parseInt(formData.num_infants) || 0,
        }],
        contact_info: {
          name: formData.contact_name,
          phone: formData.contact_phone,
          email: formData.customer_email,
        },
        emergency_contact: {
          name: formData.contact_name,
          phone: formData.contact_phone,
          relationship: 'Self'
        },
        passengers: [passengerData],
        addons: [],
        payment_amount: parseFloat(formData.payment_amount),
        payment_method: formData.payment_method || 'cash',
        special_requests: formData.special_requests || ''
      };

      const response = await api.createBooking(bookingData);
      
      if (response.error) {
        showError(new Error(response.error));
      } else {
        showSuccess('Walk-in booking created! Booking #: ' + response.booking_number);
        closeModal();
        loadAllData();
      }
    } catch (error) {
      console.error('Error creating walk-in booking:', error);
      showError(new Error('Failed to create booking. Please try again.'));
    }
  };

  const handleCreateManualPayment = async () => {
    try {
      console.log('Form Data:', formData);
      console.log('Booking ID:', formData.booking_id);
      console.log('Booking Search:', formData.booking_search);
      
      if (!formData.booking_search || formData.booking_search.length < 5) {
        showError(new Error('Please enter the last 5 digits of the booking number'));
        return;
      }

      if (!formData.booking_id || formData.booking_id === '') {
        showError(new Error('Please select a booking from the dropdown'));
        return;
      }

      if (!formData.payment_amount || !formData.payment_method) {
        showError(new Error('Please fill in payment amount and method'));
        return;
      }

      const bookingId = parseInt(formData.booking_id);
      console.log('Parsed Booking ID:', bookingId);
      
      const booking = data.bookings.find(b => b.id === bookingId);
      console.log('Found Booking:', booking);
      
      if (!booking) {
        showError(new Error('Booking not found. Please search again.'));
        return;
      }

      const email = booking.customer?.email || booking.contact_email;
      
      await api.addPayment(booking.id, {
        amount: formData.payment_amount,
        payment_method: formData.payment_method,
        remarks: formData.payment_remarks || ''
      }, email);

      showSuccess('Payment added successfully!');
      closeModal();
      loadAllData();
    } catch (error) {
      console.error('Error adding manual payment:', error);
      showError(error);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await adminApi.deletePackage(packageId);
      showSuccess('Package deleted!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting package:', error);
      showError(new Error('Failed to delete package'));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await adminApi.deleteCategory(categoryId);
      showSuccess('Category deleted!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting category:', error);
      showError(new Error('Failed to delete category'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await adminApi.deleteItem(itemId);
      showSuccess('Item deleted!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting item:', error);
      showError(new Error('Failed to delete item'));
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await adminApi.markContactMessageRead(messageId, true);
      showSuccess('Message marked as read');
      loadAllData();
    } catch (error) {
      showError(error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this contact message?')) return;
    try {
      await adminApi.deleteContactMessage(messageId);
      showSuccess('Message deleted');
      loadAllData();
    } catch (error) {
      showError(error);
    }
  };

  const openDiscountForm = (code = null) => {
    if (code) {
      setDiscountForm({
        id: code.id,
        code: code.code,
        discount_type: code.discount_type,
        discount_value: code.discount_value,
        min_purchase_amount: code.min_purchase_amount || '0',
        max_discount_amount: code.max_discount_amount || '',
        usage_limit: code.usage_limit || '',
        valid_from: code.valid_from ? code.valid_from.slice(0, 16) : '',
        valid_until: code.valid_until ? code.valid_until.slice(0, 16) : '',
        is_active: code.is_active
      });
    } else {
      const now = new Date();
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setDiscountForm({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_purchase_amount: '0',
        max_discount_amount: '',
        usage_limit: '',
        valid_from: now.toISOString().slice(0, 16),
        valid_until: nextMonth.toISOString().slice(0, 16),
        is_active: true
      });
    }
  };

  const handleSaveDiscountCode = async () => {
    if (!discountForm?.code || !discountForm?.discount_value) {
      showError(new Error('Code and discount value are required'));
      return;
    }
    try {
      const payload = {
        code: discountForm.code.toUpperCase(),
        discount_type: discountForm.discount_type,
        discount_value: discountForm.discount_value,
        min_purchase_amount: discountForm.min_purchase_amount || 0,
        max_discount_amount: discountForm.max_discount_amount || null,
        usage_limit: discountForm.usage_limit || null,
        valid_from: new Date(discountForm.valid_from).toISOString(),
        valid_until: new Date(discountForm.valid_until).toISOString(),
        is_active: discountForm.is_active
      };
      if (discountForm.id) {
        await adminApi.updateDiscountCode(discountForm.id, payload);
        showSuccess('Discount code updated');
      } else {
        await adminApi.createDiscountCode(payload);
        showSuccess('Discount code created');
      }
      setDiscountForm(null);
      loadAllData();
    } catch (error) {
      showError(error);
    }
  };

  const handleDeleteDiscountCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await adminApi.deleteDiscountCode(codeId);
      showSuccess('Discount code deleted');
      loadAllData();
    } catch (error) {
      showError(error);
    }
  };

  const handleExportPackages = async () => {
    try {
      await adminApi.exportPackages();
      showSuccess('Packages exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      showError(error.message || 'Failed to export packages');
    }
  };

  const handleImportPackages = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      showError('Please select a CSV file');
      event.target.value = ''; // Reset file input
      return;
    }
    
    try {
      const result = await adminApi.importPackages(file);
      showSuccess(result.message || 'Packages imported successfully!');
      loadAllData();
    } catch (error) {
      console.error('Error importing packages:', error);
      showError(error.message || 'Failed to import packages');
    } finally {
      event.target.value = ''; // Reset file input
    }
  };

  const handleViewPassengers = async (packageId) => {
    try {
      const data = await adminApi.getPackagePassengers(packageId);
      setPassengers(data.passengers || []);
      setSelectedItem({ id: packageId, name: data.package_name, total: data.total_passengers });
      setModalType('view-passengers');
      setShowModal(true);
    } catch (error) {
      console.error('Error loading passengers:', error);
      showError(new Error('Failed to load passengers'));
    }
  };

  const handleExportPassengers = (packageId) => {
    adminApi.exportPackagePassengers(packageId);
  };

  const handleViewRoomingList = async (packageId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/qr/rooming-list/${packageId}/`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load rooming list');
      }
      const payload = await res.json();
      const data = payload.rooming_data || payload;
      const pkg = data.package || {};
      const rooms = data.rooms || {};
      const roomEntries = Object.entries(rooms);

      if (roomEntries.length === 0) {
        showError(new Error('No room assignments found for this package'));
        return;
      }

      const travelDate = pkg.travel_date
        ? new Date(pkg.travel_date).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })
        : 'TBA';

      const roomsHtml = roomEntries.map(([roomKey, room], idx) => {
        const customers = room.customers || [];
        const rows = customers.map((c, i) => `
          <tr>
            <td style="padding:8px 10px;border:1px solid #ddd;text-align:center">${i + 1}</td>
            <td style="padding:8px 10px;border:1px solid #ddd;font-weight:600">${c.name || '—'}</td>
            <td style="padding:8px 10px;border:1px solid #ddd">${c.booking_number || '—'}</td>
            <td style="padding:8px 10px;border:1px solid #ddd;text-transform:capitalize">${c.passenger_type || '—'}</td>
            <td style="padding:8px 10px;border:1px solid #ddd;text-transform:capitalize">${c.gender || '—'}</td>
            <td style="padding:8px 10px;border:1px solid #ddd">${c.phone || '—'}</td>
            <td style="padding:8px 10px;border:1px solid #ddd;font-size:12px">${c.emergency_contact || '—'}</td>
          </tr>
        `).join('');

        return `
          <div style="margin-bottom:28px;page-break-inside:avoid">
            <div style="background:#1b5e20;color:#fff;padding:12px 16px;border-radius:8px 8px 0 0;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px">
              <div>
                <div style="font-size:16px;font-weight:800">${roomKey}</div>
                <div style="font-size:12px;opacity:.9;margin-top:2px">
                  Type: <strong style="text-transform:capitalize">${room.room_type || '—'}</strong>
                  &nbsp;•&nbsp; Guests: <strong>${customers.length}</strong>
                </div>
              </div>
              <div style="background:rgba(255,255,255,.15);padding:6px 12px;border-radius:20px;font-size:12px;font-weight:700">
                Room ${idx + 1} of ${roomEntries.length}
              </div>
            </div>
            <table style="width:100%;border-collapse:collapse;background:#fff;font-size:13px">
              <thead>
                <tr style="background:#e8f5e9">
                  <th style="padding:10px;border:1px solid #ddd;width:40px">#</th>
                  <th style="padding:10px;border:1px solid #ddd;text-align:left">Name</th>
                  <th style="padding:10px;border:1px solid #ddd;text-align:left">Booking #</th>
                  <th style="padding:10px;border:1px solid #ddd;text-align:left">Type</th>
                  <th style="padding:10px;border:1px solid #ddd;text-align:left">Gender</th>
                  <th style="padding:10px;border:1px solid #ddd;text-align:left">Phone</th>
                  <th style="padding:10px;border:1px solid #ddd;text-align:left">Emergency Contact</th>
                </tr>
              </thead>
              <tbody>
                ${rows || '<tr><td colspan="7" style="padding:16px;text-align:center;color:#888">No passengers in this room</td></tr>'}
              </tbody>
            </table>
          </div>
        `;
      }).join('');

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Rooming List - ${pkg.name || 'Package'}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; background: #f0f0f0; margin: 0; padding: 24px; color: #222; }
    .sheet { max-width: 1000px; margin: 0 auto; }
    @media print {
      body { background: #fff; padding: 12px; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="no-print" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;gap:12px;flex-wrap:wrap">
      <h2 style="margin:0;color:#1b5e20">Rooming List</h2>
      <button onclick="window.print()" style="padding:10px 22px;background:#1b5e20;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:bold">🖨️ Print / Save PDF</button>
    </div>

    <div style="background:#fff;border-radius:12px;padding:24px;margin-bottom:24px;box-shadow:0 2px 10px rgba(0,0,0,.08);border-top:5px solid #1b5e20">
      <div style="display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;align-items:flex-start">
        <div>
          <div style="font-size:12px;color:#1b5e20;font-weight:800;letter-spacing:1px;margin-bottom:4px">TM FOUZY TRAVEL &amp; TOURS</div>
          <h1 style="margin:0 0 8px;font-size:22px;color:#111">${pkg.name || 'Package'}</h1>
          <div style="font-size:14px;color:#555;line-height:1.7">
            <div><strong>Travel Date:</strong> ${travelDate}</div>
            <div><strong>Location:</strong> ${pkg.location || '—'}</div>
          </div>
        </div>
        <div style="display:flex;gap:12px;flex-wrap:wrap">
          <div style="background:#e8f5e9;padding:12px 18px;border-radius:10px;text-align:center;min-width:90px">
            <div style="font-size:22px;font-weight:800;color:#1b5e20">${data.total_rooms || roomEntries.length}</div>
            <div style="font-size:11px;color:#555;font-weight:600">Rooms</div>
          </div>
          <div style="background:#e3f2fd;padding:12px 18px;border-radius:10px;text-align:center;min-width:90px">
            <div style="font-size:22px;font-weight:800;color:#1565c0">${data.total_customers || 0}</div>
            <div style="font-size:11px;color:#555;font-weight:600">Passengers</div>
          </div>
        </div>
      </div>
    </div>

    ${roomsHtml}

    <div style="text-align:center;color:#888;font-size:12px;margin-top:16px">
      Generated by TM Fouzy Admin • ${new Date().toLocaleString('en-SG')}
    </div>
  </div>
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      showError(e);
    }
  };

  const handlePrintTags = async (packageId, tagType) => {
    try {
      const API = API_BASE_URL;
      const res = await fetch(`${API}/qr/bulk-tags/${packageId}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag_type: tagType })
      });
      if (!res.ok) { showError(new Error('Failed to generate tags')); return; }
      const data = await res.json();
      if (!data.tags || data.tags.length === 0) { showError(new Error('No passengers found for this package')); return; }

      const passengerCount = data.total_passengers || data.total_customers || data.tags.length;
      const tagField = tagType === 'id' ? 'id_tag' : 'bag_tag';
      const title = tagType === 'id' ? 'ID Tags' : 'Bag Tags';
      const isId = tagType === 'id';

      const tagsHtml = data.tags.map(t => isId ? `
        <!-- FRONT -->
        <div style="width:220px;background:#fff;border-radius:12px;box-shadow:0 3px 12px rgba(0,0,0,.2);overflow:hidden;page-break-inside:avoid;display:inline-block;margin:8px;vertical-align:top;border:1px solid #ddd">
          <div style="padding:10px;text-align:center;border-bottom:2px solid #c8a84b">
            <div style="display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:4px">
              <span style="font-size:24px">&#x1F54C;</span>
              <div style="text-align:left">
                <div style="font-size:14px;font-weight:900;color:#1b5e20">TM FOUZY</div>
                <div style="font-size:8px;color:#555;font-weight:600">TRAVEL &amp; TOURS PTE LTD</div>
                <div style="font-size:8px;color:#1b5e20;direction:rtl">&#x634;&#x631;&#x643;&#x629; &#x62A;.&#x645; &#x641;&#x648;&#x632;&#x64A; &#x644;&#x644;&#x633;&#x641;&#x631; &#x648;&#x627;&#x644;&#x633;&#x64A;&#x627;&#x62D;&#x629;</div>
              </div>
            </div>
            <div style="display:flex;align-items:center;justify-content:center;gap:5px;margin-top:4px">
              <span>&#x1F1F8;&#x1F1EC;</span>
              <span style="font-size:10px;direction:rtl;color:#333">&#x633;&#x646;&#x63A;&#x627;&#x641;&#x648;&#x631;&#x629;</span>
            </div>
          </div>
          <div style="display:flex;justify-content:center;margin:10px 0 6px">
            <div style="width:75px;height:75px;border-radius:50%;border:3px solid #c8a84b;background:#f5f5f5;display:flex;align-items:center;justify-content:center;font-size:30px">&#x1F464;</div>
          </div>
          <div style="text-align:center;font-size:11px;font-weight:900;color:#111;padding:0 8px;text-transform:uppercase;line-height:1.4">${t.customer_name}</div>
          <div style="text-align:center;font-size:9px;color:#555;margin:3px 0">Booking: ${t.booking_number}</div>
          ${t.id_tag ? `<img src="${t.id_tag}" style="display:block;width:80px;height:80px;margin:6px auto;border-radius:4px" />` : ''}
          <div style="background:#c8a84b;color:#1a0f00;text-align:center;font-size:11px;font-weight:900;padding:7px;margin-top:8px;letter-spacing:1px;text-transform:uppercase">${data.package_name}</div>
        </div>
        <!-- BACK -->
        <div style="width:220px;background:#f5c518;border-radius:12px;box-shadow:0 3px 12px rgba(0,0,0,.2);overflow:hidden;page-break-inside:avoid;display:inline-block;margin:8px;vertical-align:top">
          <div style="padding:14px 12px">
            <div style="font-size:13px;font-weight:900;color:#111;text-align:center;margin-bottom:10px;letter-spacing:1px">${data.package_name.toUpperCase()}</div>
            <div style="font-size:10px;color:#111;line-height:1.9">
              <div><span style="font-weight:900;text-decoration:underline">MADINAH:</span> ${data.hotel_madinah || 'Hotel TBA'}</div>
              <div style="margin-bottom:4px;color:#333">${data.tel_madinah || '+966 XXXX XXXX'}</div>
              <div><span style="font-weight:900;text-decoration:underline">MAKKAH:</span> ${data.hotel_makkah || 'Hotel TBA'}</div>
              <div style="margin-bottom:8px;color:#333">${data.tel_makkah || '+966 XXXX XXXX'}</div>
            </div>
            <div style="border:1.5px solid #333;border-radius:6px;padding:8px;text-align:center;font-size:10px;color:#111;line-height:1.8">
              <div style="font-weight:900">Tour Leader</div>
              <div>${data.tour_leader_name || 'Contact Tour Leader'}</div>
              <div>${data.tour_leader_phone || '+65 XXXX XXXX'}</div>
            </div>
          </div>
        </div>
      ` : `
        <div style="width:320px;height:200px;background:#fff;border-radius:10px;box-shadow:0 3px 12px rgba(0,0,0,.2);overflow:hidden;page-break-inside:avoid;display:inline-block;margin:10px;vertical-align:top;position:relative">
          <!-- Yellow left strip -->
          <div style="position:absolute;left:0;top:0;bottom:0;width:28px;background:#c8a84b"></div>
          <!-- Main content -->
          <div style="margin-left:28px;display:flex;height:100%">
            <!-- Left: photo + name + address -->
            <div style="flex:1;padding:10px 8px;display:flex;flex-direction:column;justify-content:center">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                <div style="width:52px;height:52px;border-radius:50%;border:2px solid #c8a84b;background:#f5f5f5;display:flex;align-items:center;justify-content:center;font-size:24px;flex-shrink:0">👤</div>
                <div>
                  <div style="font-size:11px;font-weight:900;color:#111;text-transform:uppercase;line-height:1.3">${t.customer_name}</div>
                  <div style="font-size:9px;color:#555;margin-top:2px">Booking: ${t.booking_number}${t.room_number ? ` • Room ${t.room_number}` : ''}</div>
                </div>
              </div>
              <div style="font-size:8px;color:#555;line-height:1.4">390 Victoria Street Singapore 188061</div>
              <div style="font-size:8px;color:#555">Tel: +65 6294 8044</div>
              <div style="font-size:8px;color:#1b5e20;font-weight:700;margin-top:3px">${data.package_name}</div>
            </div>
            <!-- Right: QR code + logo -->
            <div style="width:100px;border-left:1px solid #eee;padding:8px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px">
              ${t.bag_tag ? `<img src="${t.bag_tag}" style="width:80px;height:80px;border-radius:4px" />` : '<span style="font-size:28px">&#x1F54C;</span>'}
              <div style="font-size:10px;font-weight:900;color:#1b5e20;text-align:center">TM FOUZY</div>
              <div style="display:flex;align-items:center;gap:2px;margin-top:2px">
                <span style="font-size:14px">&#x1F1F8;&#x1F1EC;</span>
              </div>
            </div>
          </div>
        </div>
      `).join('');

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title} - ${data.package_name}</title>
        <style>body{font-family:Arial,sans-serif;background:#f0f0f0;padding:20px} @media print{body{background:#fff;padding:0}.no-print{display:none}}</style>
      </head><body>
        <div class="no-print" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px">
          <h2 style="margin:0;color:#1b5e20">${title} – ${data.package_name} (${passengerCount} passengers)</h2>
          <button onclick="window.print()" style="padding:10px 22px;background:#1b5e20;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:bold">🖨️ Print All</button>
        </div>
        <div>${tagsHtml}</div>
      </body></html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) { showError(e); }
  };

  const StatCard = ({ icon, title, value, color, subtitle, onClick }) => (
    <div onClick={onClick}
      className={`bg-gradient-to-br ${color} rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all cursor-pointer`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className="text-5xl opacity-80">{icon}</div>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-cream text-primary', cancelled: 'bg-red-100 text-red-800',
      processing: 'bg-purple-100 text-purple-800', shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-cream text-primary'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Error / Success Toast */}
      {errorMsg && (
        <div className={`fixed top-4 right-4 z-[9999] max-w-sm w-full shadow-2xl rounded-xl p-4 flex items-start gap-3 animate-slide-down ${
          errorMsg.startsWith('✅') ? 'bg-primary' : 'bg-red-600'
        } text-white`}>
          <span className="text-2xl flex-shrink-0">{errorMsg.startsWith('✅') ? '✅' : '❌'}</span>
          <div className="flex-1">
            <p className="font-bold text-sm mb-0.5">{errorMsg.startsWith('✅') ? 'Success' : 'Error'}</p>
            <p className="text-sm text-white/90 whitespace-pre-line">{errorMsg.replace('✅ ', '')}</p>
          </div>
          <button onClick={() => setErrorMsg('')} className="text-white/70 hover:text-white text-lg leading-none">✕</button>
        </div>
      )}
      {/* Mobile Menu Button */}
      <button 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-purple-600 text-white p-3 rounded-lg shadow-lg">
        <span className="text-2xl">{mobileMenuOpen ? '✕' : '☰'}</span>
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          onClick={() => setMobileMenuOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
        />
      )}

      {/* Left Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-purple-900 to-indigo-900 text-white shadow-2xl z-40 flex flex-col transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Header - Fixed */}
        <div className="p-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🔐</span>
            <div>
              <h2 className="text-xl font-bold">Admin Panel</h2>
              <p className="text-purple-200 text-xs">{adminUser?.username}</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto px-6 space-y-2 pb-32" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.3) transparent'}}>
            <button onClick={() => { setActiveSection('overview'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'overview' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">📊</span>
              <span>Overview</span>
            </button>

            <button onClick={() => { setActiveSection('bookings'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'bookings' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">📅</span>
              <span>Bookings</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.bookings.length}</span>
            </button>

            <button onClick={() => { setActiveSection('packages'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'packages' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">📦</span>
              <span>Packages</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.packages.length}</span>
            </button>

            <button onClick={() => { setActiveSection('orders'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'orders' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">🛍️</span>
              <span>Shop Orders</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.orders.length}</span>
            </button>

            <button onClick={() => { setActiveSection('payments'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'payments' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">💳</span>
              <span>Payments</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.payments?.length || 0}</span>
            </button>

            <button onClick={() => { setActiveSection('categories'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'categories' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">🏷️</span>
              <span>Categories</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.categories.length}</span>
            </button>

            <button onClick={() => { setActiveSection('items'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'items' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">🛒</span>
              <span>Shop Items</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.items.length}</span>
            </button>

            <button onClick={() => { setActiveSection('users'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'users' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">👥</span>
              <span>Users</span>
              <span className="ml-auto bg-white/20 px-2 py-1 rounded-full text-xs">{data.users.length}</span>
            </button>

            <button onClick={() => { setActiveSection('customers'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'customers' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">👤</span>
              <span>Customers</span>
            </button>

            <button onClick={() => { setActiveSection('documents'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'documents' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">📄</span>
              <span>Documents</span>
            </button>

            <button onClick={() => { setActiveSection('discount-codes'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'discount-codes' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">🏷️</span>
              <span>Discount Codes</span>
            </button>

            <button onClick={() => { setActiveSection('messages'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'messages' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">✉️</span>
              <span>Contact Messages
                {data.contactMessages.filter(m => !m.is_read).length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {data.contactMessages.filter(m => !m.is_read).length}
                  </span>
                )}
              </span>
            </button>

            <button onClick={() => { setActiveSection('live-audio'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'live-audio' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">🎙️</span>
              <span>Live Audio</span>
            </button>

            <button onClick={() => { setActiveSection('qr-tags'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'qr-tags' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">🎫</span>
              <span>ID & Bag Tags</span>
            </button>

            <button onClick={() => { setActiveSection('blog'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'blog' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">📰</span>
              <span>Blog &amp; News</span>
            </button>

            <button onClick={() => { setActiveSection('page-content'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                activeSection === 'page-content' ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'
              }`}>
              <span className="text-xl">📝</span>
              <span>Page Content</span>
            </button>
          </nav>

        {/* Footer Buttons - Fixed */}
        <div className="p-6 flex-shrink-0 space-y-2 border-t border-white/10">
            <button onClick={() => navigate('landing')}
              className="w-full bg-white/10 hover:bg-white/20 px-4 py-3 rounded-lg transition-all flex items-center gap-3">
              <span className="text-xl">🏠</span>
              <span>Back to Website</span>
            </button>
            <button onClick={handleLogout}
              className="w-full bg-red-500/80 hover:bg-red-600 px-4 py-3 rounded-lg transition-all flex items-center gap-3">
              <span className="text-xl">🚪</span>
              <span>Logout</span>
            </button>
          </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 p-4 md:p-8 pt-20 lg:pt-8">
        <div className="mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
            {activeSection === 'overview' && '📊 Dashboard Overview'}
            {activeSection === 'bookings' && '📅 Manage Bookings'}
            {activeSection === 'packages' && '📦 Manage Packages'}
            {activeSection === 'orders' && '🛍️ Manage Shop Orders'}
            {activeSection === 'payments' && '💳 Manage Payments'}
            {activeSection === 'categories' && '🏷️ Manage Categories'}
            {activeSection === 'items' && '🛒 Manage Shop Items'}
            {activeSection === 'users' && '👥 Manage Users'}
            {activeSection === 'customers' && '👤 Manage Customers & Tour Leaders'}
            {activeSection === 'documents' && '📄 Customer Documents'}
            {activeSection === 'discount-codes' && '🏷️ Discount Codes'}
            {activeSection === 'messages' && '✉️ Contact Messages'}
            {activeSection === 'qr-tags' && '🎫 ID & Bag Tags'}
            {activeSection === 'live-audio' && '🎙️ Live Audio Broadcast'}
            {activeSection === 'blog' && '📰 Blog & News'}
            {activeSection === 'page-content' && '📝 Page Content'}
          </h1>
          <p className="text-gray-600">Welcome back, {adminUser?.username}</p>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div>
            {/* Year Filter */}
            <div className="flex flex-wrap items-center gap-3 mb-6 bg-white rounded-xl shadow p-4">
              <span className="text-sm font-semibold text-gray-600">Stats for:</span>
              {['all', 2024, 2025, 2026, 2027].map(y => (
                <button
                  key={y}
                  onClick={() => {
                    setStatsYear(y);
                    const filtered = y === 'all' ? data.bookings : data.bookings.filter(b => new Date(b.created_at).getFullYear() === parseInt(y));
                    const revenue = filtered.reduce((sum, b) => sum + parseFloat(b.paid_amount || 0), 0);
                    setStats(prev => ({
                      ...prev,
                      totalBookings: filtered.length,
                      totalRevenue: revenue,
                      pendingBookings: filtered.filter(b => b.status === 'pending').length,
                      totalCustomers: new Set(filtered.map(b => b.customer?.email)).size,
                    }));
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    statsYear === y ? 'bg-purple-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                  }`}>
                  {y === 'all' ? 'All Time' : y}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <StatCard icon="📊" title="Total Bookings" value={stats.totalBookings}
                color="from-blue-500 to-blue-600" subtitle={`${stats.pendingBookings} pending`}
                onClick={() => setActiveSection('bookings')} />
              {adminUser?.is_superuser && (
                <StatCard icon="💰" title="Total Revenue" value={`$${stats.totalRevenue.toFixed(2)}`}
                  color="from-primary to-secondary" onClick={() => setActiveSection('bookings')} />
              )}
              <StatCard icon="👥" title="Total Customers" value={stats.totalCustomers}
                color="from-purple-500 to-purple-600" />
              <StatCard icon="📦" title="Packages" value={stats.totalPackages}
                color="from-orange-500 to-orange-600" onClick={() => setActiveSection('packages')} />
              <StatCard icon="🛍️" title="Shop Orders" value={stats.totalOrders}
                color="from-pink-500 to-pink-600" onClick={() => setActiveSection('orders')} />
              <StatCard icon="⏳" title="Pending Bookings" value={stats.pendingBookings}
                color="from-yellow-500 to-yellow-600" onClick={() => setActiveSection('bookings')} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span>📅</span> Recent Bookings
                </h3>
                <div className="space-y-3">
                  {data.bookings.slice(0, 5).map(booking => (
                    <div key={booking.id} className="border-l-4 border-blue-500 pl-4 py-2 hover:bg-gray-50 transition-all rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{booking.booking_number}</p>
                          <p className="text-sm text-gray-600">{booking.package_name}</p>
                          <p className="text-xs text-gray-500">{booking.customer?.email}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(booking.status)}`}>
                            {booking.status}
                          </span>
                          <p className="text-sm font-bold text-primary mt-1">${booking.total_amount}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <span>🛍️</span> Recent Orders
                </h3>
                <div className="space-y-3">
                  {data.orders.slice(0, 5).map(order => (
                    <div key={order.id} className="border-l-4 border-purple-500 pl-4 py-2 hover:bg-gray-50 transition-all rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{order.order_number}</p>
                          <p className="text-xs text-gray-500">{order.customer?.email}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(order.status)}`}>
                            {order.status}
                          </span>
                          <p className="text-sm font-bold text-purple-600 mt-1">${order.total_amount}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Section */}
        {activeSection === 'bookings' && (
          <div>
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => openModal('add-booking')}
                className="bg-gradient-to-r from-primary to-secondary hover:from-secondary hover:to-ink text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2">
                <span className="text-xl">➕</span>
                Add Walk-in Booking
              </button>
              <input
                type="text"
                placeholder="Search by name, email, booking #..."
                value={bookingSearch}
                onChange={e => setBookingSearch(e.target.value)}
                className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              />
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold">Booking #</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Customer Name</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Package</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Pax</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Total</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Paid</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Balance</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.bookings
                      .filter(b => {
                        if (!bookingSearch) return true;
                        const q = bookingSearch.toLowerCase();
                        return (
                          b.booking_number?.toLowerCase().includes(q) ||
                          b.contact_name?.toLowerCase().includes(q) ||
                          b.customer?.email?.toLowerCase().includes(q) ||
                          b.package_name?.toLowerCase().includes(q)
                        );
                      })
                      .map(booking => {
                        const totalPax = (booking.rooms || []).reduce((sum, r) =>
                          sum + (r.num_adults || 0) + (r.num_children || 0) + (r.num_infants || 0), 0);
                        return (
                        <tr key={booking.id} className="hover:bg-blue-50 transition-all">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{booking.booking_number}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {booking.contact_name || booking.customer?.email?.split('@')[0] || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{booking.customer?.email}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{booking.package_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-center">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{totalPax || '—'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={booking.status}
                              onChange={(e) => handleUpdateBookingStatus(booking.id, e.target.value)}
                              className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${getStatusBadge(booking.status)}`}>
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">${booking.total_amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-semibold">${booking.paid_amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-semibold">${booking.balance_amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(booking.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                            <button
                              onClick={() => openModal('view-booking', booking)}
                              className="text-blue-600 hover:text-blue-800 font-medium">
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteBooking(booking.id)}
                              className="text-red-600 hover:text-red-800 font-medium">
                              Delete
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Packages Section */}
        {activeSection === 'packages' && (
          <div>
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => openModal('add-package')}
                className="bg-gradient-to-r from-primary to-secondary hover:from-secondary hover:to-ink text-white px-4 md:px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center justify-center gap-2 text-sm md:text-base">
                <span className="text-xl">➕</span>
                Add New Package
              </button>
              <button
                onClick={handleExportPackages}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 md:px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center justify-center gap-2 text-sm md:text-base">
                <span className="text-xl">📥</span>
                Export Packages
              </button>
              <label className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 md:px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer text-sm md:text-base">
                <span className="text-xl">📤</span>
                Import Packages
                <input type="file" accept=".csv" onChange={handleImportPackages} className="hidden" />
              </label>
            </div>

            {/* Category Tabs */}
            <div className="mb-4 border-b-2 border-gray-200 -mx-4 px-4 md:mx-0 md:px-0">
              <div className="flex gap-0 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveCategoryTab('all')}
                  className={`px-4 md:px-8 py-4 font-semibold transition-all whitespace-nowrap border-b-4 text-sm md:text-base ${
                    activeCategoryTab === 'all' 
                      ? 'border-purple-600 text-purple-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}>
                  All Packages
                </button>
                {data.categories.filter(cat => cat.category_type !== 'item').map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategoryTab(category.slug)}
                    className={`px-4 md:px-8 py-4 font-semibold transition-all whitespace-nowrap border-b-4 text-sm md:text-base ${
                      activeCategoryTab === category.slug 
                        ? 'border-purple-600 text-purple-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}>
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { key: 'all', label: 'All', color: 'bg-gray-600' },
                { key: 'active', label: '✅ Active (Online)', color: 'bg-primary' },
                { key: 'inactive', label: '🚫 Inactive (Hidden)', color: 'bg-red-500' },
                { key: 'featured', label: '⭐ Featured', color: 'bg-yellow-500' },
              ].map(f => (
                <button key={f.key} onClick={() => setPackageStatusFilter(f.key)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold text-white transition-all ${
                    packageStatusFilter === f.key ? f.color + ' shadow-lg scale-105' : 'bg-gray-300 text-gray-700'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.packages
              .filter(pkg => {
                if (activeCategoryTab === 'all') return true;
                const activeCategory = data.categories.find(cat => cat.slug === activeCategoryTab);
                if (!activeCategory) return true;
                // pkg.category can be a nested object {id, name} or a plain ID number
                const pkgCategoryId = pkg.category?.id ?? pkg.category;
                return pkgCategoryId === activeCategory.id || pkg.category_name === activeCategory.name;
              })
              .filter(pkg => {
                if (packageStatusFilter === 'active') return pkg.is_active;
                if (packageStatusFilter === 'inactive') return !pkg.is_active;
                if (packageStatusFilter === 'featured') return pkg.is_featured;
                return true;
              })
              .map(pkg => (
              <div key={pkg.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all">
                {pkg.featured_image && (
                  <div className="relative">
                    <img src={pkg.featured_image} alt={pkg.name} className="w-full h-48 object-cover" />
                    <div className="absolute top-2 left-2 flex gap-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${pkg.is_active ? 'bg-primary text-white' : 'bg-red-500 text-white'}`}>
                        {pkg.is_active ? '✅ Online' : '🚫 Hidden'}
                      </span>
                      {pkg.is_featured && <span className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold">⭐ Featured</span>}
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">{pkg.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{pkg.short_description}</p>
                  
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-sm text-gray-500">From</p>
                      <p className="text-2xl font-bold text-primary">${pkg.min_price}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{pkg.duration_days}D/{pkg.duration_nights}N</p>
                      <p className="text-sm font-medium">{new Date(pkg.travel_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg mb-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Target</p>
                        <p className="text-lg font-bold text-blue-600">{pkg.max_capacity || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Registered</p>
                        <p className="text-lg font-bold text-primary">{pkg.registered_pax || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Balance</p>
                        <p className="text-lg font-bold text-orange-600">{pkg.balance_seats || 0}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${pkg.max_capacity > 0 ? ((pkg.registered_pax || 0) / pkg.max_capacity * 100) : 0}%` }}>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 text-center mt-1">
                        {pkg.max_capacity > 0 ? Math.round((pkg.registered_pax || 0) / pkg.max_capacity * 100) : 0}% Full
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      onClick={() => handleViewPassengers(pkg.id)}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      👥 Passengers
                    </button>
                    <button
                      onClick={() => handleExportPassengers(pkg.id)}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      📥 Export
                    </button>
                    <button
                      onClick={() => handlePrintTags(pkg.id, 'id')}
                      className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      🎫 ID Tags
                    </button>
                    <button
                      onClick={() => handlePrintTags(pkg.id, 'bag')}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      🧳 Bag Tags
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_BASE_URL}/admin/packages/${pkg.id}/`);
                          const fullPkg = await res.json();
                          const categoryId = fullPkg.category?.id ?? fullPkg.category ?? '';
                          openModal('edit-package', { ...fullPkg, category: categoryId });
                        } catch {
                          const categoryId = data.categories.find(c => c.name === pkg.category_name)?.id || '';
                          openModal('edit-package', {...pkg, category: categoryId});
                        }
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-all">
                      ✏️ Edit
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_BASE_URL}/admin/packages/${pkg.id}/`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ is_active: !pkg.is_active })
                          });
                          if (res.ok) { showSuccess(`Package ${pkg.is_active ? 'hidden' : 'published'}!`); loadAllData(); }
                          else showError(new Error('Failed to update status'));
                        } catch(e) { showError(e); }
                      }}
                      className={`px-3 py-2 rounded-lg font-medium transition-all text-white text-sm ${pkg.is_active ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-primary hover:bg-secondary'}`}>
                      {pkg.is_active ? '👁️ Hide' : '👁️ Show'}
                    </button>
                    <button
                      onClick={() => handleDeletePackage(pkg.id)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all">
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        )}

        {/* Orders Section */}
        {activeSection === 'orders' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">Order #</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.orders.map(order => (
                    <tr key={order.id} className="hover:bg-purple-50 transition-all">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.order_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {order.shipping_name || order.customer_email || order.customer?.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select 
                          value={order.status}
                          onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${getStatusBadge(order.status)}`}>
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">${order.total_amount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={() => openModal('view-order', order)}
                          className="text-blue-600 hover:text-blue-800 font-medium">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments Section */}
        {activeSection === 'payments' && (
          <div>
            <div className="mb-6">
              <button
                onClick={() => openModal('add-payment')}
                className="bg-gradient-to-r from-primary to-secondary hover:from-secondary hover:to-ink text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2">
                <span className="text-xl">➕</span>
                Add Manual Payment
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">Payment #</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Booking #</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Method</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Screenshot</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(data.payments || []).map(payment => {
                    const booking = data.bookings.find(b => b.id === payment.booking);
                    return (
                    <tr key={payment.id} className="hover:bg-cream transition-all">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payment.payment_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => openModal('view-booking', booking)}
                          className="text-blue-600 hover:text-blue-800 font-semibold underline">
                          {booking?.booking_number || payment.booking}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {payment.customer_name || booking?.contact_name || payment.customer_email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-primary">${payment.amount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{payment.payment_method}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(payment.status)}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.payment_screenshot ? (
                          <button
                            onClick={() => window.open(payment.payment_screenshot, '_blank')}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                            View 🖼️
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">No screenshot</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        {payment.status === 'pending' && adminUser?.is_superuser && (
                          <>
                            <button
                              onClick={() => handleUpdatePaymentStatus(payment.id, 'completed')}
                              className="bg-primary hover:bg-secondary text-white px-3 py-1 rounded font-medium">
                              ✓ Accept
                            </button>
                            <button
                              onClick={() => handleUpdatePaymentStatus(payment.id, 'rejected')}
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded font-medium">
                              ✕ Reject
                            </button>
                          </>
                        )}
                        {payment.status === 'pending' && !adminUser?.is_superuser && (
                          <span className="text-yellow-600 font-medium text-xs">Awaiting approval</span>
                        )}
                        {payment.status === 'completed' && (
                          <span className="text-primary font-medium">Accepted</span>
                        )}
                        {payment.status === 'rejected' && (
                          <span className="text-red-600 font-medium">Rejected</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

        {/* Categories Section */}
        {activeSection === 'categories' && (
          <div>
            <div className="mb-6">
              <button
                onClick={() => openModal('add-category')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2">
                <span className="text-xl">➕</span>
                Add New Category
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.categories.map(cat => (
              <div key={cat.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all">
                {cat.image && (
                  <img src={cat.image} alt={cat.name} className="w-full h-32 object-cover" />
                )}
                <div className="p-6">
                  <h3 className="text-lg font-bold mb-2 text-gray-900">{cat.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{cat.description}</p>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal('edit-category', cat)}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        )}

        {/* Items Section */}
        {activeSection === 'items' && (
          <div>
            <div className="mb-6">
              <button
                onClick={() => openModal('add-item')}
                className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2">
                <span className="text-xl">➕</span>
                Add New Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.items.map(item => (
              <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all">
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-full h-48 object-cover" />
                )}
                <div className="p-6">
                  <h3 className="text-lg font-bold mb-2 text-gray-900">{item.name}</h3>
                  <p className="text-2xl font-bold text-primary mb-2">${item.price}</p>
                  <p className="text-sm text-gray-600 mb-4">Stock: {item.stock_quantity}</p>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal('edit-item', item)}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        )}

        {/* Users Section */}
        {activeSection === 'users' && (
          <div>
            <div className="mb-6">
              <button
                onClick={() => openModal('add-user')}
                className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2">
                <span className="text-xl">➕</span>
                Add New User
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold">Username</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Staff</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Active</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.users.map(user => (
                      <tr key={user.id} className="hover:bg-indigo-50 transition-all">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.first_name} {user.last_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.is_staff ? 'bg-cream text-primary' : 'bg-gray-100 text-gray-800'}`}>
                            {user.is_staff ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-cream text-primary' : 'bg-red-100 text-red-800'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.date_joined).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Customers Section */}
        {activeSection === 'customers' && (
          <div>
            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">💡 Tip:</span> Mark customers as "Tour Leader" to assign them to packages. 
                Tour leaders will appear in the dropdown when creating/editing packages.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Phone</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Display Name</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Bookings</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Tour Leader</th>
                      <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.customers && data.customers.length > 0 ? (
                      data.customers.map(customer => (
                        <tr key={customer.id} className="hover:bg-cyan-50 transition-all">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{customer.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{customer.phone}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {customer.display_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {data.bookings.filter(b => b.customer?.id === customer.id).length}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${customer.is_tour_leader ? 'bg-cream text-primary' : 'bg-gray-100 text-gray-800'}`}>
                              {customer.is_tour_leader ? '✓ Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={async () => {
                                try {
                                  await adminApi.toggleTourLeader(customer.id);
                                  alert(`Customer ${customer.is_tour_leader ? 'removed from' : 'marked as'} tour leader!`);
                                  loadAllData();
                                } catch (error) {
                                  console.error('Error toggling tour leader:', error);
                                  showError(new Error('Failed to update tour leader status'));
                                }
                              }}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                customer.is_tour_leader 
                                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                                  : 'bg-primary hover:bg-secondary text-white'
                              }`}>
                              {customer.is_tour_leader ? 'Remove Tour Leader' : 'Mark as Tour Leader'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                          No customers found. Customers are created when they make bookings.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Documents Section */}
        {activeSection === 'documents' && (
          <div>
            <DocumentUploadSection customers={data.customers || []} packages={data.packages || []} bookings={data.bookings || []} />
          </div>
        )}

        {/* Discount Codes Section */}
        {activeSection === 'discount-codes' && (
          <div>
            <div className="mb-6 flex gap-3">
              <button
                onClick={() => openDiscountForm()}
                className="bg-gradient-to-r from-primary to-secondary hover:from-secondary hover:to-ink text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all">
                + Add Discount Code
              </button>
            </div>

            {discountForm && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-sand">
                <h3 className="text-lg font-bold mb-4">{discountForm.id ? 'Edit' : 'New'} Discount Code</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Code *</label>
                    <input type="text" value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      className="w-full border rounded-lg px-3 py-2" placeholder="SUMMER2026" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type *</label>
                    <select value={discountForm.discount_type}
                      onChange={(e) => setDiscountForm({ ...discountForm, discount_type: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2">
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Value *</label>
                    <input type="number" value={discountForm.discount_value}
                      onChange={(e) => setDiscountForm({ ...discountForm, discount_value: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" placeholder="10" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Min Purchase ($)</label>
                    <input type="number" value={discountForm.min_purchase_amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, min_purchase_amount: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Discount ($)</label>
                    <input type="number" value={discountForm.max_discount_amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, max_discount_amount: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Usage Limit</label>
                    <input type="number" value={discountForm.usage_limit}
                      onChange={(e) => setDiscountForm({ ...discountForm, usage_limit: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" placeholder="Unlimited" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Valid From *</label>
                    <input type="datetime-local" value={discountForm.valid_from}
                      onChange={(e) => setDiscountForm({ ...discountForm, valid_from: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Valid Until *</label>
                    <input type="datetime-local" value={discountForm.valid_until}
                      onChange={(e) => setDiscountForm({ ...discountForm, valid_until: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={discountForm.is_active}
                        onChange={(e) => setDiscountForm({ ...discountForm, is_active: e.target.checked })}
                        className="w-4 h-4" />
                      <span className="text-sm font-medium">Active</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button onClick={handleSaveDiscountCode}
                    className="bg-primary hover:bg-secondary text-white px-6 py-2 rounded-lg font-semibold">
                    Save
                  </button>
                  <button onClick={() => setDiscountForm(null)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg font-semibold">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Code</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Discount</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Used</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Valid Until</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.discountCodes.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-8 text-gray-400">No discount codes yet</td></tr>
                  ) : data.discountCodes.map(code => (
                    <tr key={code.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-bold">{code.code}</td>
                      <td className="px-4 py-3">
                        {code.discount_type === 'percentage' ? `${code.discount_value}%` : `$${code.discount_value}`}
                      </td>
                      <td className="px-4 py-3">{code.times_used}{code.usage_limit ? ` / ${code.usage_limit}` : ''}</td>
                      <td className="px-4 py-3 text-sm">{new Date(code.valid_until).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${code.is_active ? 'bg-cream text-primary' : 'bg-gray-100 text-gray-600'}`}>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openDiscountForm(code)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium">Edit</button>
                          <button onClick={() => handleDeleteDiscountCode(code.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contact Messages Section */}
        {activeSection === 'messages' && (
          <div className="space-y-4">
            {data.contactMessages.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400">
                No contact messages yet
              </div>
            ) : data.contactMessages.map(msg => (
              <div key={msg.id} className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${msg.is_read ? 'border-gray-300' : 'border-blue-500'}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-lg">{msg.subject}</h3>
                    <p className="text-sm text-gray-600">{msg.name} · {msg.email}{msg.phone ? ` · ${msg.phone}` : ''}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                  </div>
                  {!msg.is_read && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-semibold">New</span>
                  )}
                </div>
                <p className="text-gray-700 whitespace-pre-wrap mb-4">{msg.message}</p>
                <div className="flex gap-3">
                  {!msg.is_read && (
                    <button onClick={() => handleMarkMessageRead(msg.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                      Mark as Read
                    </button>
                  )}
                  <a href={`mailto:${msg.email}?subject=Re: ${msg.subject}`}
                    className="bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg text-sm font-semibold">
                    Reply via Email
                  </a>
                  <button onClick={() => handleDeleteMessage(msg.id)}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg text-sm font-semibold">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live Audio Section */}
        {activeSection === 'live-audio' && (
          <LiveAudioSection packages={data.packages} API_BASE={API_BASE_URL} showError={showError} showSuccess={showSuccess} />
        )}

        {/* Blog & News Section */}
        {activeSection === 'blog' && (
          <BlogManagementSection showError={showError} showSuccess={showSuccess} />
        )}

        {activeSection === 'page-content' && (
          <PageContentEditor showError={showError} showSuccess={showSuccess} />
        )}

        {/* QR Tags Section */}
        {activeSection === 'qr-tags' && (
          <div>
            {/* Info Banner */}
            <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-6 mb-8 text-white flex items-center gap-4">
              <div className="text-5xl">🎫</div>
              <div>
                <h2 className="text-xl font-bold mb-1">ID & Bag Tag Generator</h2>
                <p className="text-purple-100 text-sm">Generate and print ID tags or Bag tags for all passengers in a package. Tags include QR codes for quick scanning.</p>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm border border-purple-100">
                <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                <span className="text-sm font-medium text-gray-700">🎫 ID Tag – Name, Booking #, Emergency Contact, QR</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm border border-orange-100">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span className="text-sm font-medium text-gray-700">🧳 Bag Tag – Name, Room #, Hotel, Package QR</span>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm border border-blue-100">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm font-medium text-gray-700">📋 Rooming List – Full PDF with all room assignments</span>
              </div>
            </div>

            {/* Package Cards */}
            {data.packages.filter(p => p.is_active).length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-12 text-center">
                <div className="text-6xl mb-4">📦</div>
                <p className="text-gray-500 text-lg">No active packages found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {data.packages.filter(p => p.is_active).map(pkg => (
                  <div key={pkg.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border border-gray-100">
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4">
                      {pkg.featured_image && (
                        <img src={pkg.featured_image} alt={pkg.name} className="w-full h-28 object-cover rounded-lg mb-3 opacity-80" />
                      )}
                      <h3 className="font-bold text-white text-base leading-tight">{pkg.name}</h3>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-300">📅 {new Date(pkg.travel_date).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">Active</span>
                      </div>
                    </div>

                    {/* Passenger Count */}
                    <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">👥</span>
                        <div>
                          <p className="text-xs text-gray-500">Registered Passengers</p>
                          <p className="font-bold text-gray-900 text-lg">
                            {data.bookings.filter(b => b.package_name === pkg.name).reduce((sum, b) => sum + (b.rooms?.reduce((rs, r) => rs + (r.passengers?.length || 0), 0) || 0), 0) || '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">📅</span>
                        <div>
                          <p className="text-xs text-gray-500">Bookings</p>
                          <p className="font-bold text-gray-900 text-lg">{data.bookings.filter(b => b.package_name === pkg.name).length}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="p-4 space-y-2">
                      <button
                        onClick={() => handlePrintTags(pkg.id, 'id')}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md">
                        <span>🎫</span> Print ID Tags (All Passengers)
                      </button>
                      <button
                        onClick={() => handlePrintTags(pkg.id, 'bag')}
                        className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md">
                        <span>🧳</span> Print Bag Tags (All Passengers)
                      </button>
                      <button
                        onClick={() => handleViewRoomingList(pkg.id)}
                        className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md">
                        <span>📋</span> View Rooming List
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-primary to-secondary text-white p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                {modalType === 'view-booking' && '👁️ View Booking Details'}
                {modalType === 'view-order' && '👁️ View Order Details'}
                {modalType === 'view-passengers' && '👥 Passenger List'}
                {modalType.includes('add') && `➕ Add New ${modalType.split('-')[1]}`}
                {modalType.includes('edit') && `✏️ Edit ${modalType.split('-')[1]}`}
              </h2>
              <button onClick={closeModal} className="text-white hover:bg-white/20 p-2 rounded-lg transition-all">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {/* View Booking/Order Details */}
              {(modalType === 'view-booking' || modalType === 'view-order' || modalType === 'view-passengers') && selectedItem ? (
                <div className="space-y-6">
                  {modalType === 'view-passengers' && (
                    <>
                      <div className="bg-indigo-50 p-4 rounded-lg">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">👥 Passenger List</h3>
                        <p className="text-gray-700"><span className="font-semibold">Package:</span> {selectedItem.name}</p>
                        <p className="text-gray-700"><span className="font-semibold">Total Passengers:</span> {selectedItem.total}</p>
                      </div>

                      <div className="bg-white rounded-lg overflow-hidden border-2 border-gray-200">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold">Booking #</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">Full Name</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">DOB</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">Phone</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">Passport #</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">Passport Expiry</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">Issue Date</th>
                                <th className="px-4 py-3 text-left text-xs font-bold">Documents</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {passengers.map((passenger, idx) => (
                                <tr key={idx} className="hover:bg-indigo-50">
                                  <td className="px-4 py-3 text-sm">{passenger.booking_number}</td>
                                  <td className="px-4 py-3 text-sm font-medium">{passenger.full_name}</td>
                                  <td className="px-4 py-3 text-sm">{passenger.date_of_birth}</td>
                                  <td className="px-4 py-3 text-sm">{passenger.phone}</td>
                                  <td className="px-4 py-3 text-sm font-semibold">{passenger.passport_number}</td>
                                  <td className="px-4 py-3 text-sm">{passenger.passport_expiry}</td>
                                  <td className="px-4 py-3 text-sm">{passenger.passport_issue_date || 'N/A'}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <div className="flex flex-col gap-2">
                                      <DocLink url={passenger.passport_photo} label="passport" />
                                      <DocLink url={passenger.photo_id} label="photo ID" />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button 
                          onClick={() => handleExportPassengers(selectedItem.id)}
                          className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold">
                          📥 Export to CSV
                        </button>
                        <button onClick={closeModal}
                          className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold">
                          Close
                        </button>
                      </div>
                    </>
                  )}
                  {modalType === 'view-booking' && (
                    <>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">📋 Booking Info</h3>
                          <div className="space-y-2">
                            <p><span className="text-gray-600">Booking #:</span> <span className="font-semibold">{selectedItem.booking_number}</span></p>
                            <p><span className="text-gray-600">Package:</span> <span className="font-semibold">{selectedItem.package_name}</span></p>
                            <p><span className="text-gray-600">Customer:</span> <span className="font-semibold">{selectedItem.customer?.email}</span></p>
                            <p><span className="text-gray-600">Status:</span> <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedItem.status)}`}>{selectedItem.status}</span></p>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">💰 Financial</h3>
                          <div className="space-y-2">
                            <p><span className="text-gray-600">Total:</span> <span className="text-xl font-bold">${selectedItem.total_amount}</span></p>
                            <p><span className="text-gray-600">Paid:</span> <span className="text-lg font-bold text-primary">${selectedItem.paid_amount}</span></p>
                            <p><span className="text-gray-600">Balance:</span> <span className="text-lg font-bold text-red-600">${selectedItem.balance_amount}</span></p>
                          </div>
                        </div>
                      </div>
                      {(selectedItem.rooms || []).some((room) => (room.passengers || []).length > 0) && (
                        <div className="bg-indigo-50 p-4 rounded-lg">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">Passport & photo ID uploaded by customer</h3>
                          <div className="space-y-3">
                            {selectedItem.rooms.map((room) => (
                              (room.passengers || []).map((passenger) => (
                                <div key={passenger.id || `${room.id}-${passenger.full_name}`} className="bg-white p-4 rounded-lg border border-indigo-100">
                                  <p className="font-semibold text-gray-900">{passenger.full_name}</p>
                                  <p className="text-xs text-gray-500 mb-3">
                                    Room {room.room_number} · Passport {passenger.passport_number || '—'}
                                  </p>
                                  <div className="flex flex-wrap gap-4">
                                    <DocLink url={passenger.passport_photo} label="passport" />
                                    <DocLink url={passenger.photo_id} label="photo ID" />
                                  </div>
                                </div>
                              ))
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedItem.payments && selectedItem.payments.length > 0 && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">💳 Payment Screenshots</h3>
                          <div className="grid grid-cols-2 gap-4">
                            {selectedItem.payments.map((payment, idx) => (
                              <div key={idx} className="bg-white p-3 rounded-lg border-2 border-gray-200">
                                <p className="text-sm font-semibold mb-1">{payment.payment_number}</p>
                                <p className="text-xs text-gray-600 mb-2">${payment.amount} - {new Date(payment.created_at).toLocaleDateString()}</p>
                                {payment.payment_screenshot ? (
                                  <div>
                                    <img src={payment.payment_screenshot} alt="Payment" className="w-full h-40 object-cover rounded cursor-pointer hover:opacity-80" onClick={() => window.open(payment.payment_screenshot, '_blank')} />
                                    <button onClick={() => window.open(payment.payment_screenshot, '_blank')} className="mt-2 w-full bg-blue-500 text-white px-2 py-1 rounded text-xs">View Full</button>
                                  </div>
                                ) : (
                                  <div className="bg-gray-100 h-40 rounded flex items-center justify-center"><p className="text-xs text-gray-500">No screenshot</p></div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {modalType === 'view-order' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">📦 Order Info</h3>
                          <p><span className="text-gray-600">Order #:</span> <span className="font-semibold">{selectedItem.order_number}</span></p>
                          <p><span className="text-gray-600">Customer:</span> <span className="font-semibold">{selectedItem.customer?.email}</span></p>
                          <p><span className="text-gray-600">Status:</span> <span className={`font-semibold ${selectedItem.status === 'delivered' ? 'text-primary' : selectedItem.status === 'cancelled' ? 'text-red-600' : selectedItem.status === 'refunded' ? 'text-orange-600' : 'text-blue-600'}`}>{selectedItem.status?.toUpperCase()}</span></p>
                          <p><span className="text-gray-600">Amount:</span> <span className="text-xl font-bold text-primary">${selectedItem.total_amount}</span></p>
                          <p><span className="text-gray-600">Date:</span> <span className="font-medium">{new Date(selectedItem.created_at).toLocaleDateString()}</span></p>
                        </div>
                        
                        <div className="bg-cream p-4 rounded-lg">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">📍 Shipping Address</h3>
                          <p className="font-semibold">{selectedItem.shipping_name || 'N/A'}</p>
                          <p className="text-gray-700">{selectedItem.shipping_address || 'N/A'}</p>
                          <p className="text-gray-700">{selectedItem.shipping_city || ''} {selectedItem.shipping_postal_code || ''}</p>
                          <p className="text-gray-700">{selectedItem.shipping_country || ''}</p>
                          <p className="text-gray-700 mt-2"><span className="text-gray-600">Phone:</span> {selectedItem.shipping_phone || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">🛒 Order Items</h3>
                        <div className="space-y-2">
                          {selectedItem.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-white p-3 rounded">
                              <div className="flex items-center gap-3">
                                {item.item_image && <img src={item.item_image} alt={item.item_name} className="w-12 h-12 object-cover rounded" />}
                                <div>
                                  <p className="font-semibold">{item.item_name}</p>
                                  <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                                </div>
                              </div>
                              <p className="font-bold">${item.price}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">💳 Payment Screenshot</h3>
                        {selectedItem.payment_screenshot ? (
                          <div>
                            <img src={selectedItem.payment_screenshot} alt="Payment" className="w-full h-48 object-cover rounded cursor-pointer" onClick={() => window.open(selectedItem.payment_screenshot, '_blank')} />
                            <button onClick={() => window.open(selectedItem.payment_screenshot, '_blank')} className="mt-2 w-full bg-blue-500 text-white px-3 py-2 rounded">View Full Size</button>
                          </div>
                        ) : (
                          <div className="bg-gray-100 h-48 rounded flex items-center justify-center"><p className="text-gray-500">No screenshot uploaded</p></div>
                        )}
                      </div>
                    </>
                  )}
                  <button onClick={closeModal} className="w-full bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-lg font-semibold">Close</button>
                </div>
              ) : (
              <>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  Fill in the form below. Required fields are marked with *
                </p>
              </div>
              
              <div className="space-y-4">
                {modalType.includes('package') && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
                      <select value={String(formData.category || '')} onChange={(e) => handleFormChange('category', e.target.value)}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                        <option value="">Select Category</option>
                        {data.categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <input type="text" placeholder="Package Name *" value={formData.name || ''} 
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <input type="text" placeholder="Slug (e.g., umrah-package-2026)" value={formData.slug || ''} 
                      onChange={(e) => handleFormChange('slug', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <textarea placeholder="Short Description" value={formData.short_description || ''} 
                      onChange={(e) => handleFormChange('short_description', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="2"></textarea>
                    <textarea placeholder="Full Description *" value={formData.description || ''} 
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="4"></textarea>
                    <input type="text" placeholder="Location (e.g., Makkah, Madinah) *" value={formData.location || ''} 
                      onChange={(e) => handleFormChange('location', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <textarea placeholder="Inclusions (what is included in the package)" 
                      value={formData.inclusions || ''} 
                      onChange={(e) => handleFormChange('inclusions', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="3"></textarea>
                    <textarea placeholder="Exclusions (what is NOT included)" 
                      value={formData.exclusions || ''} 
                      onChange={(e) => handleFormChange('exclusions', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="3"></textarea>
                    <textarea placeholder="Complimentary Items (free gifts: prayer mat, zamzam bottle, etc.)" 
                      value={formData.complimentary_items || ''} 
                      onChange={(e) => handleFormChange('complimentary_items', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="2"></textarea>
                    {/* Day-by-Day Itinerary Builder */}
                    <div className="bg-indigo-50 p-4 rounded-lg border-2 border-indigo-200">
                      <h4 className="font-bold text-indigo-900 mb-3">📅 Day-by-Day Itinerary</h4>
                      {(formData.itinerary || []).map((day, idx) => (
                        <details key={idx} className="bg-white p-3 rounded-lg mb-3 border-2 border-indigo-200">
                          <summary className="cursor-pointer font-semibold text-indigo-700 flex items-center justify-between list-none">
                            <span>Day {day.day} — {day.title || 'Untitled'}</span>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); const updated = [...(formData.itinerary||[])]; updated.splice(idx,1); handleFormChange('itinerary',updated); }} className="text-red-500 text-sm px-2 py-1 hover:bg-red-100 rounded ml-2">✕ Remove</button>
                          </summary>
                          <div className="mt-3 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input type="number" placeholder="Day #" value={day.day}
                                onChange={(e) => { const u=[...(formData.itinerary||[])]; u[idx].day=parseInt(e.target.value)||1; handleFormChange('itinerary',u); }}
                                className="border-2 border-gray-300 rounded px-3 py-2 text-sm" />
                              <input type="text" placeholder="Title (e.g., Sin - Doha)" value={day.title}
                                onChange={(e) => { const u=[...(formData.itinerary||[])]; u[idx].title=e.target.value; handleFormChange('itinerary',u); }}
                                className="border-2 border-gray-300 rounded px-3 py-2 text-sm" />
                            </div>
                            <textarea placeholder="Description (what happens this day)" value={day.description}
                              onChange={(e) => { const u=[...(formData.itinerary||[])]; u[idx].description=e.target.value; handleFormChange('itinerary',u); }}
                              className="w-full border-2 border-gray-300 rounded px-3 py-2 text-sm" rows="3"></textarea>
                          </div>
                        </details>
                      ))}
                      <button type="button" onClick={() => { const n={day:(formData.itinerary||[]).length+1,title:'',description:''}; handleFormChange('itinerary',[...(formData.itinerary||[]),n]); }}
                        className="w-full bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold">+ Add Day</button>
                      <p className="text-xs text-gray-600 mt-2">Will display as accordion on package detail page</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Featured Image</label>
                      {/* Show existing image if editing */}
                      {selectedItem?.featured_image && !formData._featured_image_file && (
                        <div className="mb-2">
                          <img src={selectedItem.featured_image} alt="Current" className="w-full h-32 object-cover rounded-lg border-2 border-gray-200" />
                          <p className="text-xs text-gray-500 mt-1">Current image — upload a new one to replace</p>
                        </div>
                      )}
                      {/* Preview newly selected file */}
                      {formData._featured_image_preview && (
                        <div className="mb-2">
                          <img src={formData._featured_image_preview} alt="Preview" className="w-full h-32 object-cover rounded-lg border-2 border-purple-300" />
                          <p className="text-xs text-purple-600 mt-1">✅ New image selected</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const preview = URL.createObjectURL(file);
                            setFormData(prev => ({ ...prev, _featured_image_file: file, _featured_image_preview: preview }));
                          }
                        }}
                        className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 cursor-pointer text-sm text-gray-600"
                      />
                      <p className="text-xs text-gray-500 mt-1">Upload JPG, PNG, or WebP image</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Travel Date *</label>
                        <input type="date" value={formData.travel_date || ''} 
                          onChange={(e) => handleFormChange('travel_date', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Return Date *</label>
                        <input type="date" value={formData.return_date || ''} 
                          onChange={(e) => handleFormChange('return_date', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Duration Days *</label>
                        <input type="number" placeholder="10" value={formData.duration_days || ''} 
                          onChange={(e) => handleFormChange('duration_days', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Duration Nights *</label>
                        <input type="number" placeholder="9" value={formData.duration_nights || ''} 
                          onChange={(e) => handleFormChange('duration_nights', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Maximum Capacity *</label>
                        <input type="number" placeholder="50" value={formData.max_capacity || ''} 
                          onChange={(e) => handleFormChange('max_capacity', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Min Deposit ($) *</label>
                        <input type="number" placeholder="100" value={formData.min_deposit_amount || ''} 
                          onChange={(e) => handleFormChange('min_deposit_amount', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="is_featured" checked={formData.is_featured || false} 
                          onChange={(e) => handleFormChange('is_featured', e.target.checked)}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500" />
                        <label htmlFor="is_featured" className="text-sm font-medium text-gray-700">Featured Package</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="pkg_is_active" 
                          checked={formData.is_active === true || formData.is_active === undefined} 
                          onChange={(e) => handleFormChange('is_active', e.target.checked)}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500" />
                        <label htmlFor="pkg_is_active" className="text-sm font-medium text-gray-700">
                          Active (visible on website)
                        </label>
                      </div>
                    </div>

                    <div className="bg-cyan-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">🏨 Hotel Details (Optional)</h4>
                      <div className="space-y-3">
                        <input type="text" placeholder="Hotel Name" value={formData.hotel_name || ''} 
                          onChange={(e) => handleFormChange('hotel_name', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Star Rating</label>
                            <select value={formData.hotel_star_rating || ''} 
                              onChange={(e) => handleFormChange('hotel_star_rating', e.target.value)}
                              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                              <option value="">Select Rating</option>
                              <option value="1">⭐ 1 Star</option>
                              <option value="2">⭐⭐ 2 Stars</option>
                              <option value="3">⭐⭐⭐ 3 Stars</option>
                              <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                              <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
                            </select>
                          </div>
                          <input type="text" placeholder="Country" value={formData.hotel_country || ''} 
                            onChange={(e) => handleFormChange('hotel_country', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Hotel Image</label>
                          {selectedItem?.hotel_image && !formData._hotel_image_file && (
                            <div className="mb-2">
                              <img src={selectedItem.hotel_image} alt="Current Hotel" className="w-full h-24 object-cover rounded-lg border-2 border-gray-200" />
                              <p className="text-xs text-gray-500 mt-1">Current image — upload to replace</p>
                            </div>
                          )}
                          {formData._hotel_image_preview && (
                            <div className="mb-2">
                              <img src={formData._hotel_image_preview} alt="Preview" className="w-full h-24 object-cover rounded-lg border-2 border-cyan-300" />
                              <p className="text-xs text-cyan-600 mt-1">✅ New hotel image selected</p>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const preview = URL.createObjectURL(file);
                                setFormData(prev => ({ ...prev, _hotel_image_file: file, _hotel_image_preview: preview }));
                              }
                            }}
                            className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer text-sm text-gray-600"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-cream p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">👨‍✈️ Tour Leader (Optional)</h4>
                      <select 
                        value={formData.tour_leader || ''} 
                        onChange={(e) => handleFormChange('tour_leader', e.target.value)}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                        <option value="">-- Select Tour Leader --</option>
                        {tourLeaders.map(leader => (
                          <option key={leader.id} value={leader.id}>
                            {leader.display_name} ({leader.email})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-600 mt-2">Select a customer marked as tour leader. Manage tour leaders in the Customers section.</p>
                    </div>

                    {/* Room Prices */}
                    <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">🛏️ Room Prices (per person)</h4>
                      <div className="space-y-3">
                        {[
                          { key: 'single', label: 'Single (1 pax)' },
                          { key: 'double', label: 'Double Sharing (2 pax)' },
                          { key: 'triple', label: 'Triple Sharing (3 pax)' },
                          { key: 'quad', label: 'Quad Sharing (4 pax)' },
                          { key: 'child_with_bed', label: 'Child (With Bed)' },
                          { key: 'child_no_bed', label: 'Child (No Bed)' },
                          { key: 'infant', label: 'Infant' },
                        ].map(({ key, label }) => {
                          const existing = formData.room_prices?.find(r => r.sharing_type === key);
                          const priceKey = `room_price_${key}`;
                          const availKey = `room_avail_${key}`;
                          return (
                            <div key={key} className="flex items-center gap-3">
                              <div className="w-40 text-sm font-medium text-gray-700">{label}</div>
                              <input
                                type="number"
                                placeholder="Price $"
                                min="0"
                                step="0.01"
                                value={formData[priceKey] ?? (existing?.price || '')}
                                onChange={(e) => handleFormChange(priceKey, e.target.value)}
                                className="flex-1 border-2 border-gray-300 rounded-lg px-3 py-2 focus:border-yellow-500 text-sm"
                              />
                              <label className="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={formData[availKey] !== undefined ? formData[availKey] : (existing?.available ?? false)}
                                  onChange={(e) => handleFormChange(availKey, e.target.checked)}
                                  className="w-4 h-4"
                                />
                                Available
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Leave price empty to skip that room type.</p>
                    </div>
                  </>
                )}
                
                {modalType.includes('category') && (
                  <>
                    <input type="text" placeholder="Category Name *" value={formData.name || ''} 
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <input type="text" placeholder="Slug (e.g., umrah-packages)" value={formData.slug || ''} 
                      onChange={(e) => handleFormChange('slug', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <textarea placeholder="Description" value={formData.description || ''} 
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="3"></textarea>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Category Image</label>
                      {selectedItem?.image && !formData._category_image_file && (
                        <div className="mb-2">
                          <img src={selectedItem.image} alt="Current" className="w-full h-24 object-cover rounded-lg border-2 border-gray-200" />
                          <p className="text-xs text-gray-500 mt-1">Current image — upload to replace</p>
                        </div>
                      )}
                      {formData._category_image_preview && (
                        <div className="mb-2">
                          <img src={formData._category_image_preview} alt="Preview" className="w-full h-24 object-cover rounded-lg border-2 border-purple-300" />
                          <p className="text-xs text-purple-600 mt-1">✅ New image selected</p>
                        </div>
                      )}
                      <input type="file" accept="image/*"
                        onChange={(e) => { const file=e.target.files[0]; if(file){const preview=URL.createObjectURL(file); setFormData(prev=>({...prev,_category_image_file:file,_category_image_preview:preview}));}}}
                        className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer text-sm text-gray-600" />
                      <p className="text-xs text-gray-500 mt-1">Upload JPG, PNG, or WebP image</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Category Type</label>
                      <select value={formData.category_type || 'package'} 
                        onChange={(e) => handleFormChange('category_type', e.target.value)}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                        <option value="package">Package</option>
                        <option value="item">Item</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" id="is_active" checked={formData.is_active !== false} 
                        onChange={(e) => handleFormChange('is_active', e.target.checked)}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500" />
                      <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Active</label>
                    </div>
                  </>
                )}
                
                {modalType.includes('item') && (
                  <>
                    <input type="text" placeholder="Item Name *" value={formData.name || ''} 
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <input type="text" placeholder="Slug (e.g., prayer-mat)" value={formData.slug || ''} 
                      onChange={(e) => handleFormChange('slug', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <textarea placeholder="Description" value={formData.description || ''} 
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="3"></textarea>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Item Image</label>
                      {selectedItem?.image && !formData._item_image_file && (
                        <div className="mb-2">
                          <img src={selectedItem.image} alt="Current" className="w-full h-24 object-cover rounded-lg border-2 border-gray-200" />
                          <p className="text-xs text-gray-500 mt-1">Current image — upload to replace</p>
                        </div>
                      )}
                      {formData._item_image_preview && (
                        <div className="mb-2">
                          <img src={formData._item_image_preview} alt="Preview" className="w-full h-24 object-cover rounded-lg border-2 border-purple-300" />
                          <p className="text-xs text-purple-600 mt-1">✅ New image selected</p>
                        </div>
                      )}
                      <input type="file" accept="image/*"
                        onChange={(e) => { const file=e.target.files[0]; if(file){const preview=URL.createObjectURL(file); setFormData(prev=>({...prev,_item_image_file:file,_item_image_preview:preview}));}}}
                        className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer text-sm text-gray-600" />
                      <p className="text-xs text-gray-500 mt-1">Upload JPG, PNG, or WebP image</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Price *</label>
                        <input type="number" step="0.01" placeholder="99.99" value={formData.price || ''} 
                          onChange={(e) => handleFormChange('price', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Stock Quantity *</label>
                        <input type="number" placeholder="100" value={formData.stock_quantity || ''} 
                          onChange={(e) => handleFormChange('stock_quantity', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" id="item_is_active" checked={formData.is_active !== false} 
                        onChange={(e) => handleFormChange('is_active', e.target.checked)}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500" />
                      <label htmlFor="item_is_active" className="text-sm font-medium text-gray-700">Active</label>
                    </div>
                  </>
                )}
                
                {modalType.includes('user') && (
                  <>
                    <input type="text" placeholder="Username *" value={formData.username || ''} 
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <input type="email" placeholder="Email *" value={formData.email || ''} 
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                    <input type="password" placeholder="Password *" value={formData.password || ''} 
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                  </>
                )}

                {modalType.includes('booking') && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Select Package *</label>
                      <select value={formData.package_id || ''} 
                        onChange={(e) => handleFormChange('package_id', e.target.value)}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                        <option value="">Choose a package...</option>
                        {data.packages.map(pkg => (
                          <option key={pkg.id} value={pkg.id}>{pkg.name} - ${pkg.min_price}</option>
                        ))}
                      </select>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">Customer Information</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <input type="email" placeholder="Customer Email *" value={formData.customer_email || ''} 
                          onChange={(e) => handleFormChange('customer_email', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        <input type="text" placeholder="Contact Name *" value={formData.contact_name || ''} 
                          onChange={(e) => handleFormChange('contact_name', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        <input type="tel" placeholder="Contact Phone *" value={formData.contact_phone || ''} 
                          onChange={(e) => handleFormChange('contact_phone', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 col-span-2" />
                      </div>
                    </div>

                    <div className="bg-cream p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">Booking Details</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Adults</label>
                          <input type="number" min="1" placeholder="1" value={formData.num_adults || ''} 
                            onChange={(e) => handleFormChange('num_adults', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Children</label>
                          <input type="number" min="0" placeholder="0" value={formData.num_children || ''} 
                            onChange={(e) => handleFormChange('num_children', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Infants</label>
                          <input type="number" min="0" placeholder="0" value={formData.num_infants || ''} 
                            onChange={(e) => handleFormChange('num_infants', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Room Type</label>
                        <select value={formData.room_type || 'double'} 
                          onChange={(e) => handleFormChange('room_type', e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                          <option value="single">Single</option>
                          <option value="double">Double</option>
                          <option value="triple">Triple</option>
                          <option value="quad">Quad</option>
                        </select>
                      </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">Passenger Details</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input type="text" placeholder="Full Name *" value={formData.passenger_name || ''} 
                            onChange={(e) => handleFormChange('passenger_name', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                          <input type="date" placeholder="Date of Birth *" value={formData.passenger_dob || ''} 
                            onChange={(e) => handleFormChange('passenger_dob', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <select value={formData.passenger_gender || ''} 
                            onChange={(e) => handleFormChange('passenger_gender', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                            <option value="">Select Gender *</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                          <input type="tel" placeholder="Phone Number *" value={formData.passenger_phone || ''} 
                            onChange={(e) => handleFormChange('passenger_phone', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <input type="text" placeholder="Passport Number *" value={formData.passenger_passport || ''} 
                            onChange={(e) => handleFormChange('passenger_passport', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                          <input type="date" placeholder="Passport Expiry *" value={formData.passenger_passport_expiry || ''} 
                            onChange={(e) => handleFormChange('passenger_passport_expiry', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                          <input type="date" placeholder="Passport Issue Date" value={formData.passenger_passport_issue || ''} 
                            onChange={(e) => handleFormChange('passenger_passport_issue', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">Payment Information</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Amount *</label>
                          <input type="number" step="0.01" placeholder="0.00" value={formData.payment_amount || ''} 
                            onChange={(e) => handleFormChange('payment_amount', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                          <select value={formData.payment_method || 'cash'} 
                            onChange={(e) => handleFormChange('payment_method', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                            <option value="cash">Cash</option>
                            <option value="paynow">PayNow</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="credit_card">Credit Card</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <textarea placeholder="Special Requests (Optional)" value={formData.special_requests || ''} 
                      onChange={(e) => handleFormChange('special_requests', e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" rows="3"></textarea>
                  </>
                )}

                {modalType === 'add-payment' && (
                  <>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-3">💳 Manual Payment Entry</h4>
                      <p className="text-sm text-gray-600 mb-4">Add a payment received at the shop or office</p>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Booking Number (Last 5 digits) *</label>
                          <input 
                            type="text" 
                            placeholder="Enter last 5 digits (e.g., 12345)" 
                            maxLength="5"
                            value={formData.booking_search || ''} 
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              // Find matching bookings
                              const matches = data.bookings.filter(b => 
                                b.booking_number.slice(-5) === value || b.booking_number.includes(value)
                              );
                              setFormData(prev => ({
                                ...prev,
                                booking_search: value,
                                booking_matches: matches,
                                booking_id: '' // Reset selection when search changes
                              }));
                            }}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" 
                          />
                        </div>

                        {formData.booking_matches && formData.booking_matches.length > 0 && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Booking *</label>
                            <select 
                              value={formData.booking_id || ''} 
                              onChange={(e) => {
                                console.log('Selected booking ID:', e.target.value);
                                setFormData(prev => ({...prev, booking_id: e.target.value}));
                              }}
                              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                              <option value="">Choose from matches...</option>
                              {formData.booking_matches.map(booking => (
                                <option key={booking.id} value={booking.id}>
                                  {booking.booking_number} - {booking.contact_name} - ${booking.balance_amount} remaining
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Amount *</label>
                            <input type="number" step="0.01" placeholder="0.00" value={formData.payment_amount || ''} 
                              onChange={(e) => handleFormChange('payment_amount', e.target.value)}
                              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method *</label>
                            <select value={formData.payment_method || 'cash'} 
                              onChange={(e) => handleFormChange('payment_method', e.target.value)}
                              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                              <option value="cash">Cash</option>
                              <option value="paynow">PayNow</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="credit_card">Credit Card</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks / Notes (Optional)</label>
                          <textarea 
                            placeholder="Add any notes about this payment..." 
                            value={formData.payment_remarks || ''} 
                            onChange={(e) => handleFormChange('payment_remarks', e.target.value)}
                            className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-200" 
                            rows="3">
                          </textarea>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button onClick={handleSubmitForm}
                  className="flex-1 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all">
                  Save
                </button>
                <button onClick={closeModal}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-all">
                  Cancel
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -- Live Audio Section Component ------------------------------
function LiveAudioSection({ packages, API_BASE, showError, showSuccess }) {
  const [sessions, setSessions] = React.useState([]);
  const [selectedPkg, setSelectedPkg] = React.useState('');
  const [title, setTitle] = React.useState('Live Briefing from Tour Leader');
  const [loading, setLoading] = React.useState(false);

  const fetchSessions = async (pkgId) => {
    if (!pkgId) return;
    try {
      const res = await fetch(`${API_BASE}/live-audio/active/?package_id=${pkgId}`);
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (_) { setSessions([]); }
  };

  const getSelectedPackage = () => packages?.find((p) => String(p.id) === String(selectedPkg));

  const handleStart = async () => {
    if (!selectedPkg) { showError(new Error('Please select a package')); return; }
    const pkg = getSelectedPackage();
    const tourLeaderEmail = pkg?.tour_leader_email;
    if (!tourLeaderEmail) {
      showError(new Error('Assign a tour leader to this package before starting live audio.'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/live-audio/start/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: selectedPkg, tour_leader_email: tourLeaderEmail, title })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      showSuccess(`🔴 Live started! ${data.customers_notified} passengers notified. Channel: ${data.channel_name}`);
      fetchSessions(selectedPkg);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  };

  const handleEnd = async (sessionId) => {
    const pkg = getSelectedPackage();
    const tourLeaderEmail = pkg?.tour_leader_email;
    if (!tourLeaderEmail) {
      showError(new Error('Tour leader email not found for this package.'));
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/live-audio/end/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, tour_leader_email: tourLeaderEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      showSuccess(`Session ended. Duration: ${Math.round(data.duration_minutes)} min, Listeners: ${data.total_listeners}`);
      fetchSessions(selectedPkg);
    } catch (e) { showError(e); }
  };

  return (
    <div>
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-6 mb-8 text-white flex items-center gap-4">
        <div className="text-5xl">🎙️</div>
        <div>
          <h2 className="text-xl font-bold mb-1">Live Audio Broadcast</h2>
          <p className="text-red-100 text-sm">Start a live audio session for your group. All passengers in the package will be notified on their mobile app.</p>
        </div>
      </div>

      {/* Start Broadcast */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h3 className="text-lg font-bold mb-4">🎙️ Start New Broadcast</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Package</label>
            <select value={selectedPkg} onChange={e => { setSelectedPkg(e.target.value); fetchSessions(e.target.value); }}
              className="w-full border-2 border-gray-300 rounded-lg px-3 py-2">
              <option value="">-- Select Package --</option>
              {packages?.filter(p => p.is_active).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Broadcast Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-3 py-2"
              placeholder="e.g. Morning Briefing, Tawaf Instructions..." />
          </div>
        </div>
        <button onClick={handleStart} disabled={loading || !selectedPkg}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50">
          {loading ? '⏳ Starting...' : '🔴 Start Live Broadcast'}
        </button>
      </div>

      {/* Active Sessions */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">📡 Active Sessions</h3>
          <button onClick={() => fetchSessions(selectedPkg)} className="text-sm text-red-600 hover:underline">Refresh</button>
        </div>
        {sessions.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <div className="text-4xl mb-2">🔇</div>
            <p>No active sessions. Start a broadcast above.</p>
          </div>
        ) : sessions.map(s => (
          <div key={s.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-xl mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
                <span className="text-xs font-bold text-red-600">LIVE</span>
              </div>
              <p className="font-bold text-gray-900">{s.title}</p>
              <p className="text-sm text-gray-500">🎧 {s.listener_count} listening – Channel: {s.channel_name}</p>
            </div>
            <button onClick={() => handleEnd(s.id)}
              className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">
              ⏹ End
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Blog Management Section ──────────────────────────────────────────────────
function BlogManagementSection({ showError, showSuccess }) {
  const BLOG_CATEGORIES = [
    { value: 'umrah-tips',    label: 'Umrah Tips' },
    { value: 'packing-guide', label: 'Packing Guide' },
    { value: 'visa-updates',  label: 'Visa Updates' },
    { value: 'dua-guide',     label: 'Dua Guide' },
    { value: 'news',          label: 'News & Announcements' },
    { value: 'travel-guide',  label: 'Travel Guide' },
    { value: 'hajj-tips',     label: 'Hajj Tips' },
  ];

  const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=400&h=250&fit=crop';

  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editPost, setEditPost] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filterCat, setFilterCat] = React.useState('all');
  const [filterStatus, setFilterStatus] = React.useState('all');
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState('');
  const [form, setForm] = React.useState({
    title: '', slug: '', category: 'news', excerpt: '',
    content: '', author_name: 'TM Fouzy Travel & Tours',
    tags: '', meta_description: '', is_published: false, is_featured: false,
  });

  React.useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getBlogPosts();
      setPosts(Array.isArray(data) ? data : []);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  };

  const openNew = () => {
    setEditPost(null);
    setImageFile(null);
    setImagePreview('');
    setForm({
      title: '', slug: '', category: 'news', excerpt: '',
      content: '', author_name: 'TM Fouzy Travel & Tours',
      tags: '', meta_description: '', is_published: false, is_featured: false,
    });
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditPost(post);
    setImageFile(null);
    setImagePreview(post.featured_image || '');
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      category: post.category || 'news',
      excerpt: post.excerpt || '',
      content: post.content || '',
      author_name: post.author_name || 'TM Fouzy Travel & Tours',
      tags: post.tags || '',
      meta_description: post.meta_description || '',
      is_published: post.is_published || false,
      is_featured: post.is_featured || false,
    });
    setShowForm(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const autoSlug = (title) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSave = async () => {
    if (!form.title.trim()) { showError(new Error('Title is required')); return; }
    if (!form.content.trim()) { showError(new Error('Content is required')); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (!form.slug) fd.set('slug', autoSlug(form.title));
      if (imageFile) fd.append('featured_image', imageFile);

      if (editPost) {
        await adminApi.updateBlogPost(editPost.id, fd);
        showSuccess('✅ Post updated successfully');
      } else {
        await adminApi.createBlogPost(fd);
        showSuccess('✅ Post created successfully');
      }
      setShowForm(false);
      loadPosts();
    } catch (e) { showError(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteBlogPost(post.id);
      showSuccess('✅ Post deleted');
      loadPosts();
    } catch (e) { showError(e); }
  };

  const handleTogglePublish = async (post) => {
    try {
      const res = await adminApi.toggleBlogPublish(post.id);
      setPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, is_published: res.is_published } : p));
      showSuccess(res.is_published ? '✅ Post published' : '✅ Post unpublished');
    } catch (e) { showError(e); }
  };

  const handleToggleFeatured = async (post) => {
    try {
      const res = await adminApi.toggleBlogFeatured(post.id);
      setPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, is_featured: res.is_featured } : p));
      showSuccess(res.is_featured ? '⭐ Marked as featured' : '✅ Removed from featured');
    } catch (e) { showError(e); }
  };

  const filtered = posts.filter((p) => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || (p.tags || '').toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || p.category === filterCat;
    const matchStatus = filterStatus === 'all' || (filterStatus === 'published' ? p.is_published : !p.is_published);
    return matchSearch && matchCat && matchStatus;
  });

  // ── Form Modal ───────────────────────────────────────────────
  if (showForm) {
    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700 flex items-center gap-2 mb-2 text-sm">
              ← Back to posts
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editPost ? '✏️ Edit Post' : '📝 New Blog Post'}
            </h2>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)}
              className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60">
              {saving ? 'Saving…' : '💾 Save Post'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content col */}
          <div className="lg:col-span-2 space-y-5">

            {/* Title */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, slug: autoSlug(e.target.value) }))}
                placeholder="e.g. Complete Umrah Packing Guide 2026"
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg focus:border-green-500 focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-2">Slug: <span className="font-mono text-gray-600">{form.slug || autoSlug(form.title)}</span></p>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Excerpt <span className="font-normal text-gray-400">(shown on listing page)</span></label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                rows={3}
                maxLength={500}
                placeholder="A short, engaging summary that appears on the blog listing page..."
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{form.excerpt.length}/500</p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Content * <span className="font-normal text-gray-400">(HTML supported)</span>
              </label>
              <textarea
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                rows={20}
                placeholder={`Write your article here. HTML is supported.\n\nExamples:\n<h2>Section Heading</h2>\n<p>Your paragraph text...</p>\n<ul><li>Bullet point</li></ul>\n<strong>Bold text</strong>`}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 font-mono text-sm focus:border-green-500 focus:outline-none resize-y"
              />
            </div>
          </div>

          {/* Sidebar col */}
          <div className="space-y-5">

            {/* Publish Settings */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">⚙️ Publish Settings</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm font-medium text-gray-700">Published</span>
                  <div
                    onClick={() => setForm((f) => ({ ...f, is_published: !f.is_published }))}
                    className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${form.is_published ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_published ? 'translate-x-7' : 'translate-x-1'}`} />
                  </div>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm font-medium text-gray-700">Featured on landing</span>
                  <div
                    onClick={() => setForm((f) => ({ ...f, is_featured: !f.is_featured }))}
                    className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${form.is_featured ? 'bg-yellow-400' : 'bg-gray-300'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_featured ? 'translate-x-7' : 'translate-x-1'}`} />
                  </div>
                </label>
              </div>
            </div>

            {/* Category & Author */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">📂 Category & Author</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-green-500 focus:outline-none">
                    {BLOG_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Author Name</label>
                  <input
                    type="text"
                    value={form.author_name}
                    onChange={(e) => setForm((f) => ({ ...f, author_name: e.target.value }))}
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">🖼️ Featured Image</h3>
              {imagePreview && (
                <img src={imagePreview} alt="Preview" className="w-full h-36 object-cover rounded-xl mb-3"
                  onError={(e) => { e.target.src = PLACEHOLDER_IMG; }} />
              )}
              <label className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-4 cursor-pointer hover:border-green-400 transition-colors">
                <span className="text-2xl">📸</span>
                <span className="text-sm text-gray-500">{imageFile ? imageFile.name : 'Click to upload image'}</span>
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            </div>

            {/* SEO */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">🔍 SEO</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Tags <span className="font-normal text-gray-400">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                    placeholder="umrah, singapore, 2026, tips"
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Meta Description <span className="font-normal text-gray-400">(max 160)</span>
                  </label>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))}
                    maxLength={160}
                    rows={3}
                    placeholder="Brief description for search engines..."
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-green-500 focus:outline-none resize-none text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">{form.meta_description.length}/160</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ── Post List ────────────────────────────────────────────────
  return (
    <div>
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Posts', value: posts.length, color: 'bg-blue-50 text-blue-700' },
          { label: 'Published', value: posts.filter((p) => p.is_published).length, color: 'bg-green-50 text-green-700' },
          { label: 'Drafts', value: posts.filter((p) => !p.is_published).length, color: 'bg-yellow-50 text-yellow-700' },
          { label: 'Featured', value: posts.filter((p) => p.is_featured).length, color: 'bg-purple-50 text-purple-700' },
        ].map((s) => (
          <div key={s.label} className={`rounded-2xl p-4 ${s.color} shadow-sm`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-sm font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search posts..."
          className="flex-1 min-w-[160px] border-2 border-gray-200 rounded-xl px-4 py-2 text-sm focus:border-green-500 focus:outline-none"
        />
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-green-500 focus:outline-none">
          <option value="all">All Categories</option>
          {BLOG_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-green-500 focus:outline-none">
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
        </select>
        <button
          onClick={openNew}
          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-xl font-semibold text-sm flex items-center gap-2">
          ✏️ New Post
        </button>
      </div>

      {/* Posts table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl h-20 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
          <p className="text-5xl mb-4">📝</p>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No posts yet</h3>
          <p className="text-gray-400 mb-6">Click "New Post" to publish your first article.</p>
          <button onClick={openNew}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold">
            ✏️ Create first post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((post) => (
            <div key={post.id}
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">

              {/* Thumbnail */}
              <img
                src={post.featured_image || PLACEHOLDER_IMG}
                alt={post.title}
                onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                className="w-full sm:w-20 h-20 object-cover rounded-xl shrink-0"
              />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    post.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {post.is_published ? 'Published' : 'Draft'}
                  </span>
                  {post.is_featured && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      ⭐ Featured
                    </span>
                  )}
                  <span className="text-[10px] font-medium text-gray-400 capitalize">
                    {BLOG_CATEGORIES.find((c) => c.value === post.category)?.label || post.category}
                  </span>
                </div>
                <p className="font-semibold text-gray-900 truncate">{post.title}</p>
                <p className="text-sm text-gray-400 mt-0.5">
                  {post.author_name} ·{' '}
                  {post.published_at
                    ? new Date(post.published_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
                    : 'Not published'}{' '}
                  · {post.read_time_minutes} min · {post.views_count} views
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <button
                  onClick={() => handleTogglePublish(post)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    post.is_published
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}>
                  {post.is_published ? '⬇ Unpublish' : '🚀 Publish'}
                </button>
                <button
                  onClick={() => handleToggleFeatured(post)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    post.is_featured
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {post.is_featured ? '★ Unfeature' : '☆ Feature'}
                </button>
                <button
                  onClick={() => openEdit(post)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all">
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDelete(post)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-all">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
